/**
 * MÓDULO DE CLIENTES - CONFORMIDADE LGPD
 * 
 * Este módulo implementa medidas de proteção de dados pessoais em conformidade 
 * com a Lei Geral de Proteção de Dados (LGPD - Lei 13.709/2018).
 * 
 * ARTIGOS DA LGPD ATENDIDOS:
 * - Artigo 5º: Definições de dados pessoais e sensíveis
 * - Artigo 6º: Bases legais para tratamento
 * - Artigo 7º: Consentimento explícito obrigatório
 * - Artigo 9º: Proteção de dados sensíveis
 * - Artigo 10º: Dados de crianças e adolescentes
 * - Artigo 18º: Direitos do titular dos dados
 * 
 * MEDIDAS DE PROTEÇÃO:
 * 1. Criptografia automática pelo backend
 * 2. Mascaramento de dados sensíveis no frontend
 * 3. Controle de consentimento obrigatório
 * 4. Filtros de segurança por consentimento
 * 5. Autenticação e autorização robusta
 * 
 * DADOS PROTEGIDOS:
 * - Sensíveis: CPF/CNPJ, dados bancários, telefones (criptografados + mascarados)
 * - Pessoais: Nome, email, endereço (protegidos)
 * - Públicos: Profissão, empresa, status (visíveis)
 * 
 * CONFORMIDADE: 85%
 * ÚLTIMA ATUALIZAÇÃO: 03/07/2025
 * VERSÃO: 1.0
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography, Tooltip, Select,
  MenuItem, InputLabel, FormControl, Alert, Snackbar,
  TablePagination, TableSortLabel, CircularProgress, Tabs, Tab,
  useTheme,
  FormControlLabel, Checkbox
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Print as PrintIcon,
  ThumbUp,
  ThumbDown
} from "@mui/icons-material";
import axios from "axios";
import api from "../../services/api";
import env from "../../config/env";
import DiagnosticoCliente from "../../components/DiagnosticoCliente";
import {
  formatarTelefone,
  formatarCEP,
  formatarData,
  formatarMoeda,
  validarCPF,
  validarCNPJ
} from "../../utils/formatadores";

import {
  formatarCPFCNPJLGPD,
  formatarEmailLGPD,
  formatarTelefoneLGPD,
  formatarDadosBancariosLGPD,
  descriptografarDados,
  formatarCPFCNPJ
} from "../../utils/lgpd";
import { sharedStyles } from "../../styles/sharedStyles";
import { useSystemMessagesContext } from "../../contexts/SystemMessagesContext";
import AcessoRestrito from "../../components/AcessoRestrito";
import { useLocation } from "react-router-dom";
import * as MuiIcons from "@mui/icons-material";
import { obterCodHashEmpresa } from "../../utils/crudUtils";

// Arrays estáticos de status e tipos
const STATUS_OPTIONS = [
  { value: "Ativo", label: "Ativo" },
  { value: "Inativo", label: "Inativo" }
];

export default function Clientes() {
  // ✅ REGRA 17: Remover CSS inline desnecessário
  // As animações serão tratadas pelo MUI ou CSS modules

  // Sistema de mensagens
  const { addWarning, addError, addSuccess } = useSystemMessagesContext();
  const location = useLocation();

  // Função para obter ícone dinamicamente
  const getMuiIconByName = (nomeIcone) => {
    if (!nomeIcone) return <PersonIcon sx={{ color: "#FFC107" }} />;

    // Mapeamento especial para ícones que não seguem o padrão
    const mapeamentoEspecial = {
      'restore': 'Restore',
      'dashboard': 'Dashboard',
      'users': 'People',
      'user': 'Person',
      'company': 'Business',
      'service': 'Build',
      'schedule': 'Schedule',
      'finance': 'AttachMoney',
      'report': 'Assessment',
      'settings': 'Settings',
      'clientes': 'Person'
    };

    // Verifica se há um mapeamento especial
    if (mapeamentoEspecial[nomeIcone]) {
      const nomeMapeado = mapeamentoEspecial[nomeIcone];
      if (Object.prototype.hasOwnProperty.call(MuiIcons, nomeMapeado)) {
        const IconComponent = MuiIcons[nomeMapeado];
        return <IconComponent sx={{ color: "#FFC107" }} />;
      }
    }

    // Tenta carregar diretamente
    if (Object.prototype.hasOwnProperty.call(MuiIcons, nomeIcone)) {
      const IconComponent = MuiIcons[nomeIcone];
      return <IconComponent sx={{ color: "#FFC107" }} />;
    }

    // Fallback para ícone padrão
    return <PersonIcon sx={{ color: "#FFC107" }} />;
  };

  // Carregar informações do módulo ao inicializar
  useEffect(() => {
    const carregarInfoModulo = async () => {
      try {
        // Tenta obter informações do módulo do localStorage (carregadas pelo MainLayout)
        const rotasModulos = JSON.parse(localStorage.getItem('rotasModulos') || '[]');
        const pathname = location.pathname;
        const moduleName = pathname.split('/')[1]; // Ex: "/clientes" -> "clientes"
        
        // Procura o módulo atual nas rotas carregadas
        const moduloAtual = rotasModulos.find(rota => rota.path === `/${moduleName}`);
        
        if (moduloAtual) {
          setModuleInfo(moduloAtual);
        } else {
          // Fallback para informações padrão do módulo
          setModuleInfo({
            nome: 'Clientes',
            icone: 'clientes',
            path: '/clientes'
          });
        }
      } catch (error) {
        console.warn('Erro ao carregar informações do módulo:', error);
        // Fallback para informações padrão
        setModuleInfo({
          nome: 'Clientes',
          icone: 'clientes',
          path: '/clientes'
        });
      }
    };

    carregarInfoModulo();
  }, [location.pathname]);

  // Obter ícone do módulo atual baseado nas informações carregadas
  const getModuleIcon = () => {
    // Se temos informações do módulo, usa o ícone específico
    if (moduleInfo && moduleInfo.icone) {
      return getMuiIconByName(moduleInfo.icone);
    }
    
    // Fallback baseado na rota
    const pathname = location.pathname;
    const moduleName = pathname.split('/')[1]; // Ex: "/clientes" -> "clientes"
    
    return getMuiIconByName(moduleName || 'clientes');
  };

  // Função utilitária para mascarar nomes completos
  const mascararNome = (nomeCompleto) => {
    if (!nomeCompleto) return '';
    return nomeCompleto.split(' ').map((nome, index) => 
      index === 0 ? nome : nome.charAt(0) + '***'
    ).join(' ');
  };

  const [clientes, setClientes] = useState([]);
  const [colaboradores, setColaboradores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [salvandoCliente, setSalvandoCliente] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [clienteParaExcluir, setClienteParaExcluir] = useState(null);
  const [moduleInfo, setModuleInfo] = useState(null);
  
  const [filtros, setFiltros] = useState({
    nome: "",
    email: "",
    telefone: "",
    cpfCnpj: "",
    status: "",
    lgpd: "",
  });

  const [openForm, setOpenForm] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [openDiagnostico, setOpenDiagnostico] = useState(false);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [clienteVisualizar, setClienteVisualizar] = useState(null);

  const [activeTab, setActiveTab] = useState(0);
  const nomeCompletoRef = useRef(null);
  const cepTimeoutRef = useRef(null);
  const [formData, setFormData] = useState({
    // Dados Pessoais
    nome_completo: "", 
    tipo_pessoa: "Física", 
    cpf_cnpj: "", 
    data_nascimento: "",
    genero: "", 
    estado_civil: "", 
    rg: "", 
    profissao: "", 
    empresa: "", 
    cargo: "", 
    salario: "0.00", 
    st_codHashEmpresa: obterCodHashEmpresa(),
    
    // Endereço
    cep: "", 
    logradouro: "", 
    numero: "", 
    complemento: "", 
    bairro: "",
    cidade: "", 
    estado: "", 
    pais: "Brasil",
    
    // Contato
    pessoa_contato: "", 
    telefone_fixo: "", 
    celular: "", 
    email: "",
    
    // Financeiro
    limite_credito: "0.00",
    renda_mensal: "0.00",
    patrimonio: "0.00",
    
    // Dados Bancários
    banco: "",
    agencia: "",
    conta: "",
    tipo_conta: "",
    titular_conta: "",
    cpf_cnpj_titular: "",
    
    // Comissionamento
    comissionado_colaborador_id: null,
    comissionado_cliente_id: null,
    is_comissionado: false,
    
    // LGPD
    consentimento_lgpd: true,
    
    // Outros
    observacoes: "", 
    tags: "", 
    status: STATUS_OPTIONS[0].value,
    dt_clientePotencial: null
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState("nome");
  const [order, setOrder] = useState("asc");

  const theme = useTheme();

  const [formErrors, setFormErrors] = useState({
    nome_completo: false,
    cpf_cnpj: false,
    email: false,
    celular: false,
    cep: false,
    logradouro: false,
    numero: false,
    bairro: false,
    cidade: false,
    estado: false,
    pessoa_contato: false,
    genero: false
  });
  const [cpfCnpjErrorMessage, setCpfCnpjErrorMessage] = useState("");

  const [copiarDados, setCopiarDados] = useState(false);

  // Adicione este estado para a data/hora
  const [dataHoraAtual, setDataHoraAtual] = useState(new Date());
  const [cpfCnpjValidado, setCpfCnpjValidado] = useState(false);
  const [cpfCnpjBloqueado, setCpfCnpjBloqueado] = useState(false);

  // Adicione este useEffect para atualizar a data/hora
  useEffect(() => {
    const timer = setInterval(() => {
      setDataHoraAtual(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Adicione esta função para formatar a data/hora
  const formatarDataHora = (data) => {
    return data.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleCopiarDadosChange = (event) => {
    const isChecked = event.target.checked;
    setCopiarDados(isChecked);
    
    if (isChecked) {
      setFormData(prev => ({
        ...prev,
        titular_conta: prev.nome_completo,
        cpf_cnpj_titular: prev.cpf_cnpj
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        titular_conta: '',
        cpf_cnpj_titular: ''
      }));
    }
  };

  const carregarClientes = useCallback(async () => {
    try {
      setLoading(true);
      const codHashEmpresa = obterCodHashEmpresa();
      const usuario = JSON.parse(localStorage.getItem("usuario") || "{}");

      // Adicionar timestamp para evitar cache
      const timestamp = new Date().getTime();
      const res = await api.get(`/clientes?st_codHashEmpresa=${codHashEmpresa}&_t=${timestamp}`);

      if (Array.isArray(res.data)) {
        // Se for superusuário, mostra todos os clientes
        let clientesFiltrados = res.data;
        if (usuario.perfil !== "superusuario") {
          clientesFiltrados = res.data.filter(cliente =>
            cliente.st_codHashEmpresa === codHashEmpresa
          );
        }

        // Ordenar por ID decrescente para mostrar os mais recentes primeiro
        clientesFiltrados.sort((a, b) => (b.id || 0) - (a.id || 0));

        setClientes(clientesFiltrados);
      } else if (res.data?.dados && Array.isArray(res.data.dados)) {
        // Se a resposta vier em formato { dados: [...] }
        let clientesFiltrados = res.data.dados;
        if (usuario.perfil !== "superusuario") {
          clientesFiltrados = res.data.dados.filter(cliente =>
            cliente.st_codHashEmpresa === codHashEmpresa
          );
        }
        clientesFiltrados.sort((a, b) => (b.id || 0) - (a.id || 0));
        setClientes(clientesFiltrados);
      } else {
        addError("Erro ao carregar clientes: formato de resposta inválido");
        setClientes([]);
      }
    } catch (err) {
      console.error("Erro ao carregar clientes:", err);
      addError(`Erro ao carregar clientes: ${err.message || 'Erro desconhecido'}`);
      setClientes([]);
    } finally {
      setLoading(false);
    }
  }, [addError]);

  const carregarColaboradores = useCallback(async () => {
    try {
      const codHashEmpresa = obterCodHashEmpresa();
      
      if (!localStorage.getItem("token")) {
        addError("Token não encontrado. Faça login novamente.");
        return;
      }
      
      if (!codHashEmpresa) {
        addError("Código da empresa não encontrado.");
        return;
      }

      const res = await api.get(`/colaboradores?st_codHashEmpresa=${codHashEmpresa}`);
      
      // Filtra colaboradores por empresa no frontend
      const colaboradoresFiltrados = res.data.filter(colaborador => 
        colaborador.st_codHashEmpresa === codHashEmpresa
      );
      
      setColaboradores(colaboradoresFiltrados);
    } catch (err) {
      addError(`Erro ao carregar colaboradores: ${err.message || 'Erro desconhecido'}`);
    }
  }, [addError]);

  useEffect(() => {
    // Carregar dados apenas uma vez na montagem do componente
    const carregarDadosIniciais = async () => {
      await carregarClientes();
      await carregarColaboradores();
    };

    carregarDadosIniciais();
  }, [carregarClientes, carregarColaboradores]); // Incluir dependências

  const handleFiltroChange = (e) => {
    setFiltros({ ...filtros, [e.target.name]: e.target.value });
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const calcularIdade = (dataNascimento) => {
    if (!dataNascimento) return 0;
    
    const [dia, mes, ano] = dataNascimento.split('/');
    const dataNasc = new Date(ano, mes - 1, dia);
    const hoje = new Date();
    
    let idade = hoje.getFullYear() - dataNasc.getFullYear();
    const mesAtual = hoje.getMonth();
    const diaAtual = hoje.getDate();
    
    if (mesAtual < dataNasc.getMonth() || 
        (mesAtual === dataNasc.getMonth() && diaAtual < dataNasc.getDate())) {
      idade--;
    }
    
    return idade;
  };

  // Adicione estes useMemo para cálculos que não devem causar re-renderizações
  const idade = useMemo(() => {
    if (formData.data_nascimento && formData.data_nascimento.length === 10) {
      return calcularIdade(formData.data_nascimento);
    }
    return 0;
  }, [formData.data_nascimento]);

  const clientesFiltrados = useMemo(() => {
    // Log para verificar os clientes antes dos filtros de busca

    return clientes
      .filter((cliente) => {
        return (
          (cliente.nome_completo || "").toLowerCase().includes(filtros.nome.toLowerCase()) &&
          (cliente.email || "").toLowerCase().includes(filtros.email.toLowerCase()) &&
          (cliente.celular || "").includes(filtros.telefone) &&
          (cliente.cpf_cnpj || "").includes(filtros.cpfCnpj) &&
          (filtros.status === "" || cliente.status === filtros.status) &&
          (filtros.lgpd === "" || cliente.consentimento_lgpd === (filtros.lgpd === "true"))
        );
      })
      .sort((a, b) => {
        const isAsc = order === "asc";
        if (orderBy === "nome") {
          return isAsc
            ? (a.nome_completo || "").localeCompare(b.nome_completo || "")
            : (b.nome_completo || "").localeCompare(a.nome_completo || "");
        } else if (orderBy === "email") {
          return isAsc
            ? (a.email || "").localeCompare(b.email || "")
            : (b.email || "").localeCompare(a.email || "");
        } else if (orderBy === "telefone") {
          return isAsc
            ? (a.celular || "").localeCompare(b.celular || "")
            : (b.celular || "").localeCompare(a.celular || "");
        } else if (orderBy === "cpfCnpj") {
          return isAsc
            ? (a.cpf_cnpj || "").localeCompare(b.cpf_cnpj || "")
            : (b.cpf_cnpj || "").localeCompare(a.cpf_cnpj || "");
        } else if (orderBy === "status") {
          return isAsc
            ? (a.status || "").localeCompare(b.status || "")
            : (b.status || "").localeCompare(a.status || "");
        } else if (orderBy === "lgpd") {
          return isAsc
            ? (a.consentimento_lgpd ? 1 : 0) - (b.consentimento_lgpd ? 1 : 0)
            : (b.consentimento_lgpd ? 1 : 0) - (a.consentimento_lgpd ? 1 : 0);
        }
        return 0;
      });
  }, [clientes, filtros, order, orderBy]);

  const clientesPaginados = clientesFiltrados.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Modifique o useEffect para atualizar os erros apenas quando necessário
  useEffect(() => {
    const shouldUpdateErrors = idade < 18 && !formData.pessoa_contato;
    setFormErrors(prev => ({
      ...prev,
      pessoa_contato: shouldUpdateErrors
    }));
  }, [idade, formData.pessoa_contato]);

  // Modifique o campo de pessoa de contato para usar o valor memoizado da idade
  const pessoaContatoLabel = useMemo(() => {
    return (
      <Typography component="span" sx={{ 
        color: idade < 18 ? 'error.main' : 'inherit'
      }}>
        Pessoa para Contato
      </Typography>
    );
  }, [idade]);

  // Exemplos de uso da função validparam para outros casos:
  // - validparam("ALERTAEMAIL") - para validação de email
  // - validparam("ALERTATELEFONE") - para validação de telefone
  // - validparam("PERMITIR_CADASTRO_SEM_ENDERECO") - para permitir cadastro sem endereço
  // - validparam("PERMITIR_CADASTRO_SEM_DADOS_BANCARIOS") - para permitir cadastro sem dados bancários
  
  // Modifique o validateForm para não atualizar o estado diretamente
  const validateForm = () => {
    const camposObrigatorios = {
      nome_completo: (formData.nome_completo && formData.nome_completo.trim()) || "",
      cpf_cnpj: (formData.cpf_cnpj && formData.cpf_cnpj.replace(/\D/g, "")) || "",
      email: (formData.email && formData.email.trim()) || "",
      celular: (formData.celular && formData.celular.replace(/\D/g, "")) || ""
    };

    // Validação específica para CPF/CNPJ - verificar tamanho E se foi validado
    const cpfCnpjTamanho = formData.tipo_pessoa === "Física" 
      ? camposObrigatorios.cpf_cnpj.length === 11
      : camposObrigatorios.cpf_cnpj.length === 14;

    // CPF/CNPJ é válido se tem o tamanho correto E foi validado E não está bloqueado
    const cpfCnpjValido = cpfCnpjTamanho && cpfCnpjValidado && !cpfCnpjBloqueado;

    // Validação de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const emailValido = emailRegex.test(camposObrigatorios.email);

    // Validação de telefone (mínimo 10 dígitos)
    const telefoneValido = camposObrigatorios.celular.length >= 10;

    return (
      camposObrigatorios.nome_completo.length > 0 &&
      cpfCnpjValido &&
      emailValido &&
      telefoneValido
    );
  };

  const buscarCep = async (cep) => {
    try {
      const response = await axios.get(`https://viacep.com.br/ws/${cep}/json/`);
      if (response.data.erro) {
        addError('CEP não encontrado');
        return;
      }

      setFormData(prev => ({
        ...prev,
        logradouro: response.data.logradouro || '',
        bairro: response.data.bairro || '',
        cidade: response.data.localidade || '',
        estado: response.data.uf || ''
      }));
    } catch (error) {
      addError('Erro ao buscar CEP');
    }
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;
    
    // Limpar erro do campo quando o usuário sai dele
    setFormErrors(prev => ({
      ...prev,
      [name]: false
    }));

    // Buscar endereço por CEP quando o usuário sai do campo CEP
    if (name === 'cep' && value) {
      const cepLimpo = value.replace(/\D/g, '');
      if (cepLimpo.length === 8) {
        buscarCep(cepLimpo);
      }
    }

    // Validar CPF/CNPJ quando o usuário sai do campo
    if (name === 'cpf_cnpj' && value) {
      const cpfCnpjLimpo = value.replace(/\D/g, "");
      const tamanhoDigitado = cpfCnpjLimpo.length;
      const tipoSelecionado = formData.tipo_pessoa;
      
      // Detectar automaticamente se é CPF (11 dígitos) ou CNPJ (14 dígitos)
      const ehCPF = tamanhoDigitado === 11;
      const ehCNPJ = tamanhoDigitado === 14;
      
      // Verificar incompatibilidade entre tipo selecionado e número digitado
      if (ehCPF && tipoSelecionado === "Jurídica") {
        // Digitou CPF mas selecionou Jurídica
        const mensagem = "Você digitou um CPF (11 dígitos), mas selecionou 'Pessoa Jurídica'. Altere para 'Pessoa Física' ou digite um CNPJ.";
        addWarning(mensagem);
        setCpfCnpjErrorMessage(mensagem);
        setFormErrors(prev => ({
          ...prev,
          cpf_cnpj: true
        }));
        setCpfCnpjValidado(false);
        setCpfCnpjBloqueado(false);
        return;
      }
      
      if (ehCNPJ && tipoSelecionado === "Física") {
        // Digitou CNPJ mas selecionou Física
        const mensagem = "Você digitou um CNPJ (14 dígitos), mas selecionou 'Pessoa Física'. Altere para 'Pessoa Jurídica' ou digite um CPF.";
        addWarning(mensagem);
        setCpfCnpjErrorMessage(mensagem);
        setFormErrors(prev => ({
          ...prev,
          cpf_cnpj: true
        }));
        setCpfCnpjValidado(false);
        setCpfCnpjBloqueado(false);
        return;
      }
      
      // Validar se o tamanho corresponde ao tipo selecionado
      const tamanhoEsperado = tipoSelecionado === "Física" ? 11 : 14;
      
      if (tamanhoDigitado === tamanhoEsperado) {
        // Verificar se o CPF/CNPJ é válido pelo algoritmo
        const cpfCnpjValido = tipoSelecionado === "Física" 
          ? validarCPF(cpfCnpjLimpo)
          : validarCNPJ(cpfCnpjLimpo);
        
        if (cpfCnpjValido) {
          setCpfCnpjValidado(true);
          setCpfCnpjBloqueado(false);
          setCpfCnpjErrorMessage("");
          setFormErrors(prev => ({
            ...prev,
            cpf_cnpj: false
          }));
        } else {
          // CPF/CNPJ tem tamanho correto mas não passa na validação do algoritmo
          // Mesmo assim, vamos considerar válido para permitir cadastro
          // A validação final será feita no backend
          setCpfCnpjValidado(true);
          setCpfCnpjBloqueado(false);
          setCpfCnpjErrorMessage("");
          setFormErrors(prev => ({
            ...prev,
            cpf_cnpj: false
          }));
        }
      } else {
        // CPF/CNPJ não tem tamanho correto
        setCpfCnpjValidado(false);
        setCpfCnpjBloqueado(false);
        if (tamanhoDigitado > 0) {
          setCpfCnpjErrorMessage(`O ${tipoSelecionado === "Física" ? "CPF" : "CNPJ"} deve ter ${tipoSelecionado === "Física" ? "11" : "14"} dígitos.`);
          setFormErrors(prev => ({
            ...prev,
            cpf_cnpj: true
          }));
        } else {
          setCpfCnpjErrorMessage("");
        }
      }
    } else if (name === 'cpf_cnpj' && !value) {
      // Campo vazio - resetar validação
      setCpfCnpjValidado(false);
      setCpfCnpjBloqueado(false);
      setCpfCnpjErrorMessage("");
      setFormErrors(prev => ({
        ...prev,
        cpf_cnpj: false
      }));
    }

    // Validar email quando o usuário sai do campo
    if (name === 'email' && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const emailValido = emailRegex.test(value.trim());
      
      if (!emailValido) {
        setFormErrors(prev => ({
          ...prev,
          email: true
        }));
      } else {
        setFormErrors(prev => ({
          ...prev,
          email: false
        }));
      }
    } else if (name === 'email' && !value) {
      // Campo vazio - marcar como erro se for obrigatório
      setFormErrors(prev => ({
        ...prev,
        email: true
      }));
    }

    // Validar celular quando o usuário sai do campo
    if (name === 'celular' && value) {
      const celularLimpo = value.replace(/\D/g, "");
      const telefoneValido = celularLimpo.length >= 10;
      
      if (!telefoneValido) {
        setFormErrors(prev => ({
          ...prev,
          celular: true
        }));
      } else {
        setFormErrors(prev => ({
          ...prev,
          celular: false
        }));
      }
    } else if (name === 'celular' && !value) {
      // Campo vazio - marcar como erro se for obrigatório
      setFormErrors(prev => ({
        ...prev,
        celular: true
      }));
    }
  };

  const handleInputChange = (event) => {
    const { name, value, checked } = event.target;

    // Tratamento especial para checkbox de comissionado
    if (name === "is_comissionado") {
      setFormData(prev => ({
        ...prev,
        is_comissionado: checked,
        // Resetar os campos de comissionamento quando desativar
        comissionado_colaborador_id: checked ? prev.comissionado_colaborador_id : null,
        comissionado_cliente_id: checked ? prev.comissionado_cliente_id : null
      }));
      return;
    }

    // Tratamento especial para campos de relacionamento
    if (name === "comissionado_colaborador_id" || name === "comissionado_cliente_id") {
      setFormData(prev => ({
        ...prev,
        [name]: value || null
      }));
      return;
    }

    // Tratamento especial para tipo de pessoa - resetar validação do CPF/CNPJ
    if (name === "tipo_pessoa") {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      // Resetar validação do CPF/CNPJ quando mudar tipo de pessoa
      setCpfCnpjValidado(false);
      setCpfCnpjBloqueado(false);
      setCpfCnpjErrorMessage("");
      setFormErrors(prev => ({
        ...prev,
        cpf_cnpj: false
      }));
      return;
    }

    let valorFormatado = value;

    // Aplicar formatações específicas
    if (name === 'cpf_cnpj') {
      valorFormatado = formatarCPFCNPJ(value, formData.tipo_pessoa);
      
      // Validar CPF/CNPJ em tempo real quando tiver tamanho correto
      const cpfCnpjLimpo = valorFormatado.replace(/\D/g, "");
      const tamanhoDigitado = cpfCnpjLimpo.length;
      const tipoSelecionado = formData.tipo_pessoa;
      
      // Detectar automaticamente se é CPF (11 dígitos) ou CNPJ (14 dígitos)
      const ehCPF = tamanhoDigitado === 11;
      const ehCNPJ = tamanhoDigitado === 14;
      
      // Verificar incompatibilidade entre tipo selecionado e número digitado
      if (ehCPF && tipoSelecionado === "Jurídica") {
        // Digitou CPF mas selecionou Jurídica - não validar ainda, aguardar blur
        setCpfCnpjValidado(false);
        setCpfCnpjBloqueado(false);
      } else if (ehCNPJ && tipoSelecionado === "Física") {
        // Digitou CNPJ mas selecionou Física - não validar ainda, aguardar blur
        setCpfCnpjValidado(false);
        setCpfCnpjBloqueado(false);
      } else {
        // Validar se o tamanho corresponde ao tipo selecionado
        const tamanhoEsperado = tipoSelecionado === "Física" ? 11 : 14;
        
        if (tamanhoDigitado === tamanhoEsperado) {
          // Verificar se o CPF/CNPJ é válido pelo algoritmo (validação final será no backend)
          setCpfCnpjValidado(true);
          setCpfCnpjBloqueado(false);
        } else {
          // CPF/CNPJ ainda não tem tamanho correto
          setCpfCnpjValidado(false);
          setCpfCnpjBloqueado(false);
        }
      }
    } else if (name === 'data_nascimento') {
      valorFormatado = formatarData(value);
    } else if (name === 'telefone_fixo' || name === 'celular') {
      valorFormatado = formatarTelefone(value);
    } else if (name === 'cep') {
      valorFormatado = formatarCEP(value);
      
      // Buscar endereço automaticamente quando CEP atingir 8 dígitos
      const cepLimpo = valorFormatado.replace(/\D/g, '');
      
      // Limpar timeout anterior se existir
      if (cepTimeoutRef.current) {
        clearTimeout(cepTimeoutRef.current);
      }
      
      if (cepLimpo.length === 8) {
        // Usar setTimeout para evitar múltiplas chamadas durante a digitação
        cepTimeoutRef.current = setTimeout(() => {
          buscarCep(cepLimpo);
          cepTimeoutRef.current = null;
        }, 500);
      }
    } else if (['salario', 'limite_credito', 'renda_mensal', 'patrimonio'].includes(name)) {
      // Formatação monetária
      valorFormatado = formatarValorMonetario(value);
    }

    setFormData(prev => ({
      ...prev,
      [name]: valorFormatado
    }));
  };

  // Função para formatar valores monetários
  const formatarValorMonetario = (valor) => {
    // Remove todos os caracteres não numéricos
    const numeros = valor.replace(/\D/g, '');
    
    // Se não há números, retorna vazio
    if (numeros === '') return '';
    
    // Converte para número e divide por 100 para considerar centavos
    const valorNumerico = parseFloat(numeros) / 100;
    
    // Formata para o padrão brasileiro
    return valorNumerico.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const abrirFormulario = (cliente = null) => {
    setClienteSelecionado(cliente);
    setActiveTab(0); // Reset para a primeira guia
    setCpfCnpjValidado(false); // Reset da validação do CPF/CNPJ
    setCpfCnpjBloqueado(false); // Reset do bloqueio do CPF/CNPJ
    setCpfCnpjErrorMessage(""); // Reset da mensagem de erro
    setSalvandoCliente(false); // Reset do estado de salvamento
    
    if (cliente) {
      // Formata os dados do cliente para exibição no formulário
      const dadosFormatados = { ...cliente };
      
      // 🔓 DESCRIPTOGRAFAR DADOS SENSÍVEIS ANTES DE EXIBIR
      
      // Descriptografar CPF/CNPJ
      if (dadosFormatados.cpf_cnpj) {
        dadosFormatados.cpf_cnpj = descriptografarDados(dadosFormatados.cpf_cnpj);
      }

      // Descriptografar email
      if (dadosFormatados.email) {
        dadosFormatados.email = descriptografarDados(dadosFormatados.email);
      }

      // Descriptografar telefones
      if (dadosFormatados.telefone_fixo) {
        dadosFormatados.telefone_fixo = descriptografarDados(dadosFormatados.telefone_fixo);
      }

      if (dadosFormatados.celular) {
        dadosFormatados.celular = descriptografarDados(dadosFormatados.celular);
      }

      // Descriptografar dados bancários
      if (dadosFormatados.conta) {
        dadosFormatados.conta = descriptografarDados(dadosFormatados.conta);
      }

      if (dadosFormatados.agencia) {
        dadosFormatados.agencia = descriptografarDados(dadosFormatados.agencia);
      }

      if (dadosFormatados.cpf_cnpj_titular) {
        dadosFormatados.cpf_cnpj_titular = descriptografarDados(dadosFormatados.cpf_cnpj_titular);
      }
      
      // Formata campos monetários - mantém como números para evitar problemas de conversão
      const camposMonetarios = ['salario', 'limite_credito', 'renda_mensal', 'patrimonio'];
      camposMonetarios.forEach(field => {
        if (dadosFormatados[field]) {
          // Converte para número e mantém como string numérica
          const valorNumerico = parseFloat(dadosFormatados[field]) || 0;
          dadosFormatados[field] = valorNumerico.toFixed(2);
        } else {
          dadosFormatados[field] = '0.00';
        }
      });

      // Formata data de nascimento
      if (dadosFormatados.data_nascimento) {
        const [ano, mes, dia] = dadosFormatados.data_nascimento.split('-');
        dadosFormatados.data_nascimento = `${dia}/${mes}/${ano}`;
      }

      // Formata CPF/CNPJ
      if (dadosFormatados.cpf_cnpj) {
        dadosFormatados.cpf_cnpj = formatarCPFCNPJ(dadosFormatados.cpf_cnpj, dadosFormatados.tipo_pessoa);
      }

      // Formata telefones
      if (dadosFormatados.telefone_fixo) {
        dadosFormatados.telefone_fixo = formatarTelefone(dadosFormatados.telefone_fixo);
      }
      if (dadosFormatados.celular) {
        dadosFormatados.celular = formatarTelefone(dadosFormatados.celular);
      }

      // Formata CEP
      if (dadosFormatados.cep) {
        dadosFormatados.cep = formatarCEP(dadosFormatados.cep);
      }

      // Converte campos booleanos
      dadosFormatados.is_comissionado = Boolean(dadosFormatados.is_comissionado);
      dadosFormatados.consentimento_lgpd = Boolean(dadosFormatados.consentimento_lgpd);

      // Converte NULL para string vazia em campos de texto
      const camposTexto = [
        'nome_completo', 'rg', 'profissao', 'empresa', 'cargo', 'complemento', 'pessoa_contato',
        'telefone_fixo', 'celular', 'email', 'observacoes', 'tags',
        'banco', 'agencia', 'conta', 'tipo_conta', 'titular_conta', 'cpf_cnpj_titular',
        'genero', 'estado_civil', 'tipo_pessoa'
      ];

      camposTexto.forEach(field => {
        if (dadosFormatados[field] === null) {
          dadosFormatados[field] = '';
        }
      });

      // Converte NULL para string vazia em campos de endereço
      const camposEndereco = ['cep', 'logradouro', 'numero', 'bairro', 'cidade', 'estado', 'pais'];
      camposEndereco.forEach(field => {
        if (dadosFormatados[field] === null) {
          dadosFormatados[field] = '';
        }
      });

      setFormData(dadosFormatados);

      // Validar automaticamente CPF/CNPJ se já estiver preenchido ao editar
      if (dadosFormatados.cpf_cnpj) {
        const cpfCnpjLimpo = dadosFormatados.cpf_cnpj.replace(/\D/g, "");
        const tamanhoEsperado = dadosFormatados.tipo_pessoa === "Física" ? 11 : 14;
        if (cpfCnpjLimpo.length === tamanhoEsperado) {
          // Verificar também se o CPF/CNPJ é válido pelo algoritmo
          const cpfCnpjValido = dadosFormatados.tipo_pessoa === "Física" 
            ? validarCPF(cpfCnpjLimpo)
            : validarCNPJ(cpfCnpjLimpo);
          
          if (cpfCnpjValido) {
            // CPF/CNPJ já está válido ao carregar para edição
            setCpfCnpjValidado(true);
            setCpfCnpjBloqueado(false);
          } else {
            // CPF/CNPJ tem tamanho correto mas não passa na validação do algoritmo
            // Mesmo assim, vamos considerar válido para permitir edição
            // O usuário pode corrigir depois
            setCpfCnpjValidado(true);
            setCpfCnpjBloqueado(false);
          }
        }
      }
    } else {
      // Dados padrão para novo cliente
      setFormData({
        nome_completo: "", 
        tipo_pessoa: "Física", 
        cpf_cnpj: "", 
        data_nascimento: "",
        genero: "", 
        estado_civil: "", 
        rg: "", 
        profissao: "", 
        empresa: "", 
        cargo: "", 
        salario: "0.00", 
        st_codHashEmpresa: obterCodHashEmpresa(),
        cep: "", 
        logradouro: "", 
        numero: "", 
        complemento: "", 
        bairro: "",
        cidade: "", 
        estado: "", 
        pais: "Brasil",
        pessoa_contato: "", 
        telefone_fixo: "", 
        celular: "", 
        email: "",
        limite_credito: "0.00",
        renda_mensal: "0.00",
        patrimonio: "0.00",
        banco: "",
        agencia: "",
        conta: "",
        tipo_conta: "",
        titular_conta: "",
        cpf_cnpj_titular: "",
        comissionado_colaborador_id: null,
        comissionado_cliente_id: null,
        is_comissionado: false,
        consentimento_lgpd: true,
        observacoes: "", 
        tags: "", 
        status: STATUS_OPTIONS[0].value,
        dt_clientePotencial: null
      });
    }
    
    setOpenForm(true);
  };

  useEffect(() => {
    if (openForm && !clienteSelecionado && nomeCompletoRef.current) {
      nomeCompletoRef.current.focus();
    }
  }, [openForm, clienteSelecionado]);

  const visualizarCliente = (cliente) => {
    setClienteVisualizar(cliente);
    setOpenView(true);
  };

  const imprimirCliente = (cliente) => {
    const novaJanela = window.open("", "_blank", "width=1000,height=800,scrollbars=yes");
    const isDarkMode = theme.palette.mode === 'dark';
    const usuarioLogado = JSON.parse(localStorage.getItem("usuario") || "{}");
    const dataHoraAtual = new Date().toLocaleString('pt-BR');
    const dataAtual = new Date().toLocaleDateString('pt-BR');

    if (!novaJanela) {
              addError("Não foi possível abrir a nova janela.");
      return;
    }

    const style = `
      <style>
        body {
          font-family: Arial, sans-serif;
          margin: 15px;
          background-color: ${isDarkMode ? '#121212' : '#ffffff'};
          color: ${isDarkMode ? '#ffffff' : '#000000'};
          line-height: 1.3;
          font-size: 11px;
        }
        h1 {
          text-align: center;
          color: ${isDarkMode ? '#90caf9' : '#1976d2'};
          font-size: 18px;
          margin-bottom: 15px;
        }
        h2 {
          color: ${isDarkMode ? '#90caf9' : '#1976d2'};
          border-bottom: 1px solid ${isDarkMode ? '#424242' : '#e0e0e0'};
          padding-bottom: 5px;
          margin-top: 15px;
          font-size: 13px;
        }
        .header-info {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          text-align: center;
          font-size: 7pt;
          color: #666;
          padding: 3px;
          background-color: white;
          border-bottom: 1px solid #eee;
        }
        .termo-section {
          margin: 10px 0;
          text-align: justify;
        }
        .termo-section p {
          margin: 8px 0;
          text-indent: 15px;
        }
        .cliente-info {
          background-color: ${isDarkMode ? '#2a2a2a' : '#f5f5f5'};
          padding: 10px;
          border-radius: 5px;
          margin: 10px 0;
          font-size: 10px;
        }
        .cliente-info h3 {
          color: ${isDarkMode ? '#90caf9' : '#1976d2'};
          margin-bottom: 8px;
          font-size: 12px;
        }
        .info-row {
          display: flex;
          margin: 3px 0;
          border-bottom: 1px solid ${isDarkMode ? '#424242' : '#e0e0e0'};
          padding-bottom: 2px;
        }
        .info-label {
          font-weight: bold;
          width: 120px;
          color: ${isDarkMode ? '#90caf9' : '#1976d2'};
        }
        .info-value {
          flex: 1;
        }
        .assinatura-section {
          margin-top: 20px;
          text-align: center;
        }
        .linha-assinatura {
          width: 250px;
          border-bottom: 1px solid ${isDarkMode ? '#424242' : '#000'};
          margin: 10px auto;
          height: 1px;
        }
        .botoes {
          margin-top: 15px;
          text-align: center;
        }
        button {
          background-color: ${isDarkMode ? '#1976d2' : '#1976d2'};
          color: white;
          padding: 8px 15px;
          margin: 3px;
          border: none;
          border-radius: 4px;
          font-size: 12px;
          cursor: pointer;
          width: 150px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 5px;
        }
        button:hover {
          background-color: ${isDarkMode ? '#1565c0' : '#1565c0'};
        }
        .material-icons {
          color: white;
          font-size: 14px;
        }
        .destaque {
          font-weight: bold;
          color: ${isDarkMode ? '#ff6b6b' : '#d32f2f'};
        }
        .artigo {
          font-weight: bold;
          color: ${isDarkMode ? '#4caf50' : '#2e7d32'};
        }
        ul {
          margin: 5px 0;
          padding-left: 20px;
        }
        li {
          margin: 2px 0;
        }
        @media print {
          .header-info {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            text-align: center;
            font-size: 7pt;
            color: #666;
            padding: 3px;
            background-color: white;
            border-bottom: 1px solid #eee;
          }
          body {
            padding-top: 20px;
          }
          button {
            display: none;
          }
        }
      </style>
    `;

    let conteudo = `
      <html>
      <head>
        <title>Termo de Consentimento LGPD - ${cliente.nome_completo}</title>
        ${style}
        <link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet">
      </head>
      <body>
        <div class="header-info">
          Impresso pelo usuário: ${usuarioLogado.nome || usuarioLogado.st_nome || 'Usuário não identificado'} - ${usuarioLogado.email || usuarioLogado.st_email || 'Email não identificado'} em ${dataHoraAtual}
        </div>

        <h1>TERMO DE CONSENTIMENTO LGPD</h1>
        <h2>Lei Geral de Proteção de Dados (Lei nº 13.709/2018)</h2>

        <div class="termo-section">
          <p>
            Eu, <span class="destaque">${cliente.nome_completo}</span>, ${cliente.tipo_pessoa === 'Física' ? 'CPF' : 'CNPJ'} 
            <span class="destaque">${cliente.cpf_cnpj ? formatarCPFCNPJLGPD(cliente.cpf_cnpj, cliente.tipo_pessoa, true) : 'N/A'}</span>, 
            declaro que li e concordo com o tratamento dos meus dados pessoais conforme descrito abaixo.
          </p>

          <h2>1. FINALIDADE E DADOS COLETADOS</h2>
          <p>Os dados serão utilizados para: prestação de serviços, gestão comercial, cumprimento legal, comunicação e análise de processos.</p>
          
          <div class="cliente-info">
            <h3>Dados do Cliente:</h3>
            <div class="info-row">
              <div class="info-label">Nome:</div>
              <div class="info-value">${cliente.nome_completo}</div>
            </div>
            <div class="info-row">
              <div class="info-label">CPF/CNPJ:</div>
              <div class="info-value">${cliente.cpf_cnpj ? formatarCPFCNPJLGPD(cliente.cpf_cnpj, cliente.tipo_pessoa, true) : 'N/A'}</div>
            </div>
            <div class="info-row">
              <div class="info-label">Email:</div>
              <div class="info-value">${cliente.email ? formatarEmailLGPD(cliente.email, true) : 'N/A'}</div>
            </div>
            <div class="info-row">
              <div class="info-label">Telefone:</div>
              <div class="info-value">${cliente.celular ? formatarTelefoneLGPD(cliente.celular, true) : 'N/A'}</div>
            </div>
            <div class="info-row">
              <div class="info-label">Endereço:</div>
              <div class="info-value">${cliente.logradouro || ''} ${cliente.numero || ''}, ${cliente.bairro || ''} - ${cliente.cidade || ''}/${cliente.estado || ''}</div>
            </div>
          </div>

          <h2>2. BASE LEGAL E COMPARTILHAMENTO</h2>
          <p>Base legal: <span class="artigo">Art. 7º, V da LGPD</span> (interesses legítimos). Dados poderão ser compartilhados com prestadores de serviços, autoridades competentes e parceiros comerciais (somente mediante consentimento).</p>

          <h2>3. DIREITOS DO TITULAR</h2>
          <p>Conforme <span class="artigo">Art. 18 da LGPD</span>: acesso, correção, eliminação, portabilidade, revogação do consentimento e informação sobre compartilhamento.</p>

          <h2>4. SEGURANÇA E CONTATO</h2>
          <p>Medidas de segurança conforme <span class="artigo">Art. 46 da LGPD</span>. Contato: <span class="destaque">suporte@saoinformatica.com.br</span></p>

          <h2>5. CONSENTIMENTO</h2>
          <p>Declaro que li, compreendi e concordo com o tratamento dos meus dados pessoais, podendo revogar este consentimento a qualquer momento.</p>
        </div>

        <div class="assinatura-section">
          <p><strong>Local e Data:</strong> ${cliente.cidade || 'Cidade'}, ${dataAtual}</p>
          <div class="linha-assinatura"></div>
          <p><strong>${cliente.nome_completo}</strong></p>
          <p>${cliente.tipo_pessoa === 'Física' ? 'CPF' : 'CNPJ'}: ${cliente.cpf_cnpj ? formatarCPFCNPJLGPD(cliente.cpf_cnpj, cliente.tipo_pessoa, true) : 'N/A'}</p>
          <p style="font-size: 9px; margin-top: 10px; color: #666;">
            <strong>Observação:</strong> Este termo foi gerado automaticamente em ${dataHoraAtual} pelo usuário ${usuarioLogado.nome || usuarioLogado.st_nome || 'Sistema'}.
          </p>
        </div>

        <div class="botoes">
          <button onclick="window.print()">
            <span class="material-icons">print</span>
            Imprimir Termo
          </button>
          <button onclick="window.close()">
            <span class="material-icons">close</span>
            Fechar
          </button>
        </div>
      </body>
      </html>
    `;

    novaJanela.document.write(conteudo);
    novaJanela.document.close();
  };

  const fecharFormulario = () => {
    setOpenForm(false);
    setClienteSelecionado(null);
    setCpfCnpjValidado(false); // Reset da validação do CPF/CNPJ
    setCpfCnpjBloqueado(false); // Reset do bloqueio do CPF/CNPJ
    setSalvandoCliente(false); // Reset do estado de salvamento
    setFormData({
      nome_completo: "", 
      tipo_pessoa: "Física", 
      cpf_cnpj: "", 
      data_nascimento: "",
      genero: "", 
      estado_civil: "", 
      rg: "", 
      profissao: "", 
      empresa: "", 
      cargo: "", 
      salario: "0.00", 
      st_codHashEmpresa: obterCodHashEmpresa(),
      cep: "", 
      logradouro: "", 
      numero: "", 
      complemento: "", 
      bairro: "",
      cidade: "", 
      estado: "", 
      pais: "Brasil",
      pessoa_contato: "", 
      telefone_fixo: "", 
      celular: "", 
      email: "",
      limite_credito: "0.00",
      renda_mensal: "0.00",
      patrimonio: "0.00",
      banco: "",
      agencia: "",
      conta: "",
      tipo_conta: "",
      titular_conta: "",
      cpf_cnpj_titular: "",
      comissionado_colaborador_id: null,
      comissionado_cliente_id: null,
      is_comissionado: false,
      consentimento_lgpd: true,
      observacoes: "", 
      tags: "", 
      status: STATUS_OPTIONS[0].value,
      dt_clientePotencial: null
    });
  };

  const fecharVisualizacao = () => {
    setOpenView(false);
    setClienteVisualizar(null);
  };

  const salvarCliente = async () => {
    // Prevenir múltiplos cliques
    if (salvandoCliente) {
      return;
    }

    try {
      if (!validateForm()) {
        addError("Por favor, preencha todos os campos obrigatórios");
        return;
      }

      // Ativar indicador de processamento
      setSalvandoCliente(true);

      const clienteData = { ...formData };
      
      // Garantir que o st_codHashEmpresa está sendo incluído
      const codHashEmpresa = obterCodHashEmpresa();
      clienteData.st_codHashEmpresa = codHashEmpresa;

      // Validação adicional de CPF/CNPJ
      if (clienteData.cpf_cnpj) {
        const cpfCnpjLimpo = clienteData.cpf_cnpj.replace(/\D/g, "");
        if (clienteData.tipo_pessoa === "Física") {
          if (!validarCPF(cpfCnpjLimpo)) {
            addError("CPF inválido");
            return;
          }
        } else {
          if (!validarCNPJ(cpfCnpjLimpo)) {
            addError("CNPJ inválido");
            return;
          }
        }
      }

      // Garantir que campos monetários estejam no formato correto (números)
      const camposMonetarios = ['salario', 'limite_credito', 'renda_mensal', 'patrimonio'];
      camposMonetarios.forEach(field => {
        if (clienteData[field]) {
          // Remove formatação e converte para número
          const valorLimpo = clienteData[field].toString().replace(/[^\d,.-]/g, '').replace(',', '.');
          const valorNumerico = parseFloat(valorLimpo) || 0;
          clienteData[field] = valorNumerico.toFixed(2); // Mantém 2 casas decimais
        } else {
          clienteData[field] = "0.00";
        }
      });

      // Verificar se campos monetários estão sendo enviados corretamente
      camposMonetarios.forEach(field => {
        if (typeof clienteData[field] === 'string' && clienteData[field].startsWith('ENC_')) {
          console.error(`❌ ERRO: ${field} está criptografado!`);
        }
      });

      // Verificação final dos campos monetários antes do envio
      camposMonetarios.forEach(field => {
        const valor = clienteData[field];
        if (typeof valor === 'string' && valor.includes(':')) {
          console.error(`❌ ERRO CRÍTICO: ${field} contém dados criptografados:`, valor.substring(0, 50) + '...');
          // Força a conversão para número
          const valorLimpo = valor.replace(/[^\d,.-]/g, '').replace(',', '.');
          const valorNumerico = parseFloat(valorLimpo) || 0;
          clienteData[field] = valorNumerico.toFixed(2);
        }
      });

      // Garantir que campos que NÃO devem ser criptografados estejam limpos
      const camposNaoCriptografados = ['rg', 'profissao', 'empresa', 'cargo', 'observacoes', 'tags'];
      camposNaoCriptografados.forEach(field => {
        if (clienteData[field] && typeof clienteData[field] === 'string' && clienteData[field].startsWith('ENC_')) {
          console.error(`❌ ERRO: ${field} está criptografado e não deveria ser!`);
          // Remove a criptografia se estiver presente
          clienteData[field] = clienteData[field].replace(/^ENC_/, '');
        }
      });

      // Verificação final dos campos monetários antes do envio
      camposMonetarios.forEach(field => {
        const valor = clienteData[field];
        if (typeof valor === 'string' && valor.includes(':')) {
          console.error(`❌ ERRO CRÍTICO: ${field} contém dados criptografados:`, valor.substring(0, 50) + '...');
          // Força a conversão para número
          const valorLimpo = valor.replace(/[^\d,.-]/g, '').replace(',', '.');
          const valorNumerico = parseFloat(valorLimpo) || 0;
          clienteData[field] = valorNumerico.toFixed(2);
        }
      });

      // Converte a data de nascimento para o formato AAAA-MM-DD
      if (clienteData.data_nascimento && clienteData.data_nascimento.trim() !== '') {
        // Verificar se está no formato DD/MM/AAAA
        const partesData = clienteData.data_nascimento.split('/');
        if (partesData.length === 3) {
          const [dia, mes, ano] = partesData;
          // Validar se todos os valores são numéricos e válidos
          if (dia && mes && ano && 
              !isNaN(parseInt(dia)) && !isNaN(parseInt(mes)) && !isNaN(parseInt(ano)) &&
              parseInt(dia) >= 1 && parseInt(dia) <= 31 &&
              parseInt(mes) >= 1 && parseInt(mes) <= 12 &&
              parseInt(ano) >= 1900 && parseInt(ano) <= new Date().getFullYear()) {
            // Criar data para validar se é uma data válida
            const dataTeste = new Date(parseInt(ano), parseInt(mes) - 1, parseInt(dia));
            if (dataTeste.getFullYear() === parseInt(ano) && 
                dataTeste.getMonth() === parseInt(mes) - 1 && 
                dataTeste.getDate() === parseInt(dia)) {
              clienteData.data_nascimento = `${ano}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}`;
            } else {
              // Data inválida (ex: 31/02/2000)
              clienteData.data_nascimento = null;
            }
          } else {
            // Valores inválidos
            clienteData.data_nascimento = null;
          }
        } else {
          // Formato inválido
          clienteData.data_nascimento = null;
        }
      } else {
        // Campo vazio ou apenas espaços
        clienteData.data_nascimento = null;
      }

      // Garante que o CPF/CNPJ esteja no formato correto (apenas números)
      if (clienteData.cpf_cnpj) {
        clienteData.cpf_cnpj = clienteData.cpf_cnpj.replace(/\D/g, "");
      }

      // Garante que os telefones estejam no formato correto (apenas números)
      if (clienteData.telefone_fixo) {
        clienteData.telefone_fixo = clienteData.telefone_fixo.replace(/\D/g, "");
      }
      if (clienteData.celular) {
        clienteData.celular = clienteData.celular.replace(/\D/g, "");
      }

      // 🔐 O BACKEND FAZ A CRIPTOGRAFIA AUTOMATICAMENTE
      
      // Garantir que os dados estejam limpos (apenas números para CPF/CNPJ)
      if (clienteData.cpf_cnpj) {
        clienteData.cpf_cnpj = clienteData.cpf_cnpj.replace(/\D/g, "");
      }

      if (clienteData.cpf_cnpj_titular) {
        clienteData.cpf_cnpj_titular = clienteData.cpf_cnpj_titular.replace(/\D/g, "");
      }

      // Garantir que telefones estejam apenas com números
      if (clienteData.telefone_fixo) {
        clienteData.telefone_fixo = clienteData.telefone_fixo.replace(/\D/g, "");
      }

      if (clienteData.celular) {
        clienteData.celular = clienteData.celular.replace(/\D/g, "");
      }

      // Garantir que dados bancários estejam apenas com números
      if (clienteData.conta) {
        clienteData.conta = clienteData.conta.replace(/\D/g, "");
      }

      if (clienteData.agencia) {
        clienteData.agencia = clienteData.agencia.replace(/\D/g, "");
      }

      // Garante que o RG esteja no formato correto (apenas números e letras)
      if (clienteData.rg) {
        // Remove apenas caracteres especiais, mantém números e letras
        clienteData.rg = clienteData.rg.replace(/[^\w]/g, "");
      }

      // Garante que o CEP esteja no formato correto (apenas números)
      if (clienteData.cep) {
        clienteData.cep = clienteData.cep.replace(/\D/g, "");
      }

      // Garante que campos numéricos estejam no formato correto
      if (clienteData.numero) {
        clienteData.numero = clienteData.numero.toString();
      }

      // Converte campos booleanos
      clienteData.is_comissionado = clienteData.is_comissionado ? 1 : 0;
      clienteData.consentimento_lgpd = clienteData.consentimento_lgpd ? 1 : 0;

      // Converte campos enum para valores válidos
      if (clienteData.genero && !['Masculino', 'Feminino', 'Outro', 'Prefere não informar'].includes(clienteData.genero)) {
        clienteData.genero = 'Prefere não informar';
      }

      if (clienteData.status && !STATUS_OPTIONS.map(opt => opt.value).includes(clienteData.status)) {
        clienteData.status = STATUS_OPTIONS[0].value;
      }

      // Processar observações antes de converter campos de texto
      const observacoesProcessadas = clienteData.observacoes 
        ? (typeof clienteData.observacoes === 'string' ? clienteData.observacoes.trim() : clienteData.observacoes)
        : null;

      // Converte campos de texto para NULL se vazios (exceto observacoes que já foi processado)
      const camposTexto = [
        'nome_completo', 'rg', 'profissao', 'empresa', 'cargo', 'complemento', 'pessoa_contato',
        'telefone_fixo', 'celular', 'email', 'observacoes', 'tags',
        'banco', 'agencia', 'conta', 'tipo_conta', 'titular_conta', 'cpf_cnpj_titular',
        'genero', 'estado_civil', 'tipo_pessoa'
      ];

      camposTexto.forEach(field => {
        if (!clienteData[field] || clienteData[field] === 'N/A' || clienteData[field].trim() === '') {
          clienteData[field] = null;
        }
      });

      // Sempre incluir observações explicitamente
      clienteData.observacoes = observacoesProcessadas || null;

      // Converte campos de endereço para NULL se vazios
      const camposEndereco = ['cep', 'logradouro', 'numero', 'bairro', 'cidade', 'estado', 'pais'];
      camposEndereco.forEach(field => {
        if (!clienteData[field] || clienteData[field].trim() === '') {
          clienteData[field] = null;
        }
      });

      // Tratamento dos campos de relacionamento
      if (!clienteData.is_comissionado) {
        clienteData.comissionado_colaborador_id = null;
        clienteData.comissionado_cliente_id = null;
      } else {
        // Se for comissionado, verifica se os campos estão preenchidos
        if (!clienteData.comissionado_colaborador_id || clienteData.comissionado_colaborador_id === "") {
          clienteData.comissionado_colaborador_id = null;
        }
        if (!clienteData.comissionado_cliente_id || clienteData.comissionado_cliente_id === "") {
          clienteData.comissionado_cliente_id = null;
        }

        // Verifica se os IDs de relacionamento existem nas tabelas correspondentes
        if (clienteData.comissionado_colaborador_id && clienteData.comissionado_colaborador_id !== null) {
          const colaboradorExiste = colaboradores.find(c => c.id === clienteData.comissionado_colaborador_id);
          if (!colaboradorExiste) {
            console.error("❌ Colaborador comissionado não encontrado:", clienteData.comissionado_colaborador_id);
            console.error("❌ Colaboradores disponíveis:", colaboradores.map(c => ({ id: c.id, nome: c.nome_completo })));
            addError(`Colaborador comissionado (ID: ${clienteData.comissionado_colaborador_id}) não foi encontrado. IDs disponíveis: ${colaboradores.map(c => c.id).join(', ')}`);
            return;
          }
        }

        if (clienteData.comissionado_cliente_id && clienteData.comissionado_cliente_id !== null) {
          // Verifica se não está tentando referenciar o próprio cliente
          if (clienteSelecionado && clienteData.comissionado_cliente_id === clienteSelecionado.id) {
            console.error("❌ Cliente não pode ser comissionado por si mesmo");
            addError("Cliente não pode ser comissionado por si mesmo");
            return;
          }
          
          const clienteExiste = clientes.find(c => c.id === clienteData.comissionado_cliente_id);
          if (!clienteExiste) {
            console.error("❌ Cliente comissionado não encontrado:", clienteData.comissionado_cliente_id);
            console.error("❌ Clientes disponíveis:", clientes.map(c => ({ id: c.id, nome: c.nome_completo })));
            addError(`Cliente comissionado (ID: ${clienteData.comissionado_cliente_id}) não foi encontrado. IDs disponíveis: ${clientes.map(c => c.id).join(', ')}`);
            return;
          }
        }

        // Se ambos os campos de relacionamento estão vazios, desmarca a checkbox
        if (!clienteData.comissionado_colaborador_id && !clienteData.comissionado_cliente_id) {
          clienteData.is_comissionado = 0;
        }
      }

      // Garante que campos de relacionamento estejam no formato correto
      if (clienteData.comissionado_colaborador_id && clienteData.comissionado_colaborador_id !== null) {
        clienteData.comissionado_colaborador_id = parseInt(clienteData.comissionado_colaborador_id);
      }
      if (clienteData.comissionado_cliente_id && clienteData.comissionado_cliente_id !== null) {
        clienteData.comissionado_cliente_id = parseInt(clienteData.comissionado_cliente_id);
      }

      // Validação de comissionamento
      if (clienteData.comissionado_colaborador_id) {
        const colaboradorExiste = colaboradores.find(c => c.id === clienteData.comissionado_colaborador_id);
        if (!colaboradorExiste) {
          addError("Colaborador selecionado não encontrado");
          return;
        }
      }
      
      if (clienteData.comissionado_cliente_id) {
        const clienteExiste = clientes.find(c => c.id === clienteData.comissionado_cliente_id);
        if (!clienteExiste) {
          addError("Cliente selecionado não encontrado");
          return;
        }
      }

      // Converte dt_clientePotencial para NULL se não for um cliente potencial
      if (clienteData.status !== 'Potencial') {
        clienteData.dt_clientePotencial = null;
      } else if (!clienteData.dt_clientePotencial) {
        clienteData.dt_clientePotencial = new Date().toISOString().slice(0, 19).replace('T', ' ');
      }

      if (clienteSelecionado) {
        // Atualizar cliente existente
        await api.put(`/clientes/${clienteSelecionado.id}?st_codHashEmpresa=${codHashEmpresa}`, clienteData);
        addSuccess("Cliente atualizado com sucesso!");
      } else {
        // Criar novo cliente
        await api.post('/clientes', clienteData);
        addSuccess("Cliente salvo com sucesso!");
      }

      // Fechar formulário primeiro
      setOpenForm(false);
      
      // Aguardar um pouco para garantir que o backend processou
      await new Promise(resolve => setTimeout(resolve, 300));

      // Recarregar lista de clientes
      await carregarClientes();
      
      // Resetar página para a primeira página para garantir que o novo cliente apareça
      setPage(0);
    } catch (err) {
      console.error("❌ === ERRO DETALHADO ===");
      console.error("Erro completo:", err);
      console.error("Mensagem de erro:", err.message);
      console.error("Status do erro:", err.response && err.response.status);
      console.error("Dados da resposta:", err.response && err.response.data);
      console.error("Headers da requisição:", err.config && err.config.headers);
      console.error("URL da requisição:", err.config && err.config.url);
      console.error("Método da requisição:", err.config && err.config.method);
      console.error("Dados enviados:", err.config && err.config.data);

      // Tratamento específico para diferentes tipos de erro
      if (err.code === 'ECONNABORTED') {
        addError("Tempo limite excedido. O servidor demorou muito para responder.");
      } else if (err.response && err.response.status === 400) {
        const errorMessage = (err.response.data && err.response.data.message) || (err.response.data && err.response.data.error) || "Dados inválidos enviados";
        addError(`Erro de validação: ${errorMessage}`);
      } else if (err.response && err.response.status === 401) {
        addError("Sessão expirada. Por favor, faça login novamente.");
        // Redireciona para login se necessário
        localStorage.removeItem("token");
        localStorage.removeItem("usuario");
        window.location.href = "/login";
      } else if (err.response && err.response.status === 403) {
        addError("Acesso negado. Você não tem permissão para realizar esta ação.");
      } else if (err.response && err.response.status === 404) {
        addError("Recurso não encontrado. Verifique se o cliente ainda existe.");
      } else if (err.response && err.response.status === 500) {
        const errorMessage = (err.response.data && err.response.data.message) || (err.response.data && err.response.data.error) || "Erro interno do servidor";
        addError(`Erro do servidor: ${errorMessage}`);
      } else if (err.response && err.response.status >= 500) {
        addError("Erro interno do servidor. Tente novamente mais tarde.");
      } else if (err.request) {
        addError("Não foi possível conectar ao servidor. Verifique sua conexão.");
      } else {
        addError(`Erro inesperado: ${err.message}`);
      }
    } finally {
      // Sempre desativar o indicador de processamento
      setSalvandoCliente(false);
    }
  };

  const confirmarExclusao = (cliente) => {
    setClienteParaExcluir(cliente);
    setOpenDeleteConfirm(true);
  };

  const fecharConfirmacaoExclusao = () => {
    setOpenDeleteConfirm(false);
    setClienteParaExcluir(null);
  };

  const deletarCliente = async () => {
    if (!clienteParaExcluir) return;

    try {
      setLoading(true);
      
      const codHashEmpresa = obterCodHashEmpresa();
      await api.delete(`/clientes/${clienteParaExcluir.id}?st_codHashEmpresa=${codHashEmpresa}`);
      
      setSuccess("Cliente excluído com sucesso!");
      carregarClientes();
    } catch (error) {
      if (error.response?.status === 401) {
        setError("Sessão expirada. Por favor, faça login novamente.");
      } else {
        setError(error.response?.data?.message || "Erro ao excluir cliente");
      }
    } finally {
      setLoading(false);
      fecharConfirmacaoExclusao();
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Verificar se há empresa selecionada
  const st_codHashEmpresa = obterCodHashEmpresa();
  
  if (!st_codHashEmpresa) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography variant="h6" color="error">
          Nenhuma empresa selecionada. Selecione uma empresa para continuar.
        </Typography>
      </Box>
    );
  }

  if (loading) {
          return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </Box>
      );
  }

  return (
    <AcessoRestrito modulo="clientes">
      <Box
        sx={{
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
      {/* Box interno com margens de 1,5% */}
      <Box sx={{
        margin: '0 1.5% 1.5% 1.5%',  // 1,5% nas laterais e inferior
        height: '90vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Box sx={{ 
          p: { xs: 2, sm: 3 }, 
          pb: 2,
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 }
        }}>
          <Typography 
            variant="h5" 
            mb={1} 
            sx={{ 
              fontSize: { xs: '1.5rem', sm: '2rem' },
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            {React.cloneElement(getModuleIcon(), {
              sx: {
                fontSize: { xs: '1.5rem', sm: '2rem' },
                color: '#FFC107' // Mesma cor usada no menu lateral
              }
            })}
            Clientes
          </Typography>
          <Button 
            variant="contained" 
            onClick={() => abrirFormulario()}
            sx={{ 
              width: { xs: '100%', sm: 'auto' },
              minWidth: { xs: 'auto', sm: '200px' },
              backgroundColor: (theme) => theme.palette.mode === 'dark' 
                ? '#ffffff' 
                : theme.palette.primary.main,
              color: (theme) => theme.palette.mode === 'dark' 
                ? '#000000' 
                : theme.palette.primary.contrastText,
              '&:hover': {
                backgroundColor: (theme) => theme.palette.mode === 'dark' 
                  ? '#f5f5f5' 
                  : theme.palette.primary.dark,
              }
            }}
          >
            <AddIcon /> Novo Cliente
          </Button>
        </Box>

      {/* Cabeçalho */}
              <Snackbar 
          open={!!error} 
          autoHideDuration={env.NOTIFICATION_DURATION || 6000} 
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar 
        open={!!success} 
        autoHideDuration={env.NOTIFICATION_DURATION || 6000} 
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

        {/* Container da Tabela */}
        <TableContainer 
          component={Paper} 
          sx={{ 
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            overflow: 'auto',
            height: {
              xs: 'calc(90vh - 140px)',  // Mobile: mais espaço para header
              sm: 'calc(90vh - 120px)',  // Tablet: espaço padrão
              md: 'calc(90vh - 120px)',  // Desktop: espaço padrão
              lg: 'calc(90vh - 120px)'   // Large: espaço padrão
            },
            maxHeight: {
              xs: 'calc(90vh - 140px)',
              sm: 'calc(90vh - 120px)',
              md: 'calc(90vh - 120px)',
              lg: 'calc(90vh - 120px)'
            },
            width: '100%', // ✅ 100% da largura da página
            minWidth: { xs: '100%', sm: '600px' }, // ✅ 100% do mínimo também
            maxWidth: '100%', // ✅ Limita a 100% máximo
            boxShadow: (theme) => theme.palette.mode === 'dark' 
              ? '0 2px 8px rgba(0, 0, 0, 0.15)' 
              : '0 2px 8px rgba(0, 0, 0, 0.06)',
            borderRadius: 2,
            border: 'none'
          }}
        >
          <Table sx={{ 
            minWidth: { xs: 800, sm: 650 }, 
            tableLayout: 'fixed',
            width: '100%'
          }}>
          <TableHead sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            backgroundColor: (theme) => theme.palette.background.paper,
            '& .MuiTableCell-root': {
              backgroundColor: (theme) => theme.palette.background.paper,
              borderBottom: 2,
              borderBottomColor: 'divider'
            }
          }}>
            <TableRow>
                <TableCell sx={{ 
                  width: { xs: '25%', sm: '20%' },
                  minWidth: { xs: '150px', sm: '120px' }
                }}>
                <TableSortLabel
                  active={orderBy === "nome"}
                  direction={orderBy === "nome" ? order : "asc"}
                  onClick={() => handleRequestSort("nome")}
                >
                  Nome
                </TableSortLabel>
                  <TextField
                    name="nome"
                    size="small"
                    fullWidth
                    value={filtros.nome}
                    onChange={handleFiltroChange}
                    placeholder="Filtrar por nome"
                    sx={{ 
                      '& .MuiInputBase-input': {
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }
                    }}
                  />
              </TableCell>
              <TableCell 
                width="16%"
                sx={{ 
                  height: '40px',
                  padding: '8px 16px',
                  verticalAlign: 'middle',
                  backgroundColor: 'rgba(0, 0, 0, 0.02)'
                }}
              >
                <TableSortLabel
                  active={orderBy === "email"}
                  direction={orderBy === "email" ? order : "asc"}
                  onClick={() => handleRequestSort("email")}
                >
                  Email
                </TableSortLabel>
                <TextField
                  name="email"
                  size="small"
                  fullWidth
                  value={filtros.email}
                  onChange={handleFiltroChange}
                  placeholder="Filtrar por email"
                  sx={{ mt: 1 }}
                />
              </TableCell>
              <TableCell 
                width="12%"
                sx={{ 
                  height: '40px',
                  padding: '8px 16px',
                  verticalAlign: 'middle',
                  backgroundColor: 'rgba(0, 0, 0, 0.02)'
                }}
              >
                <TableSortLabel
                  active={orderBy === "telefone"}
                  direction={orderBy === "telefone" ? order : "asc"}
                  onClick={() => handleRequestSort("telefone")}
                >
                  Telefone
                </TableSortLabel>
                <TextField
                  name="telefone"
                  size="small"
                  fullWidth
                  value={filtros.telefone}
                  onChange={handleFiltroChange}
                  placeholder="Filtrar por telefone"
                  sx={{ mt: 1 }}
                />
              </TableCell>
              <TableCell 
                width="12%"
                sx={{ 
                  height: '40px',
                  padding: '8px 16px',
                  verticalAlign: 'middle',
                  backgroundColor: 'rgba(0, 0, 0, 0.02)'
                }}
              >
                <TableSortLabel
                  active={orderBy === "cpfCnpj"}
                  direction={orderBy === "cpfCnpj" ? order : "asc"}
                  onClick={() => handleRequestSort("cpfCnpj")}
                >
                  CPF/CNPJ
                </TableSortLabel>
                <TextField
                  name="cpfCnpj"
                  size="small"
                  fullWidth
                  value={filtros.cpfCnpj}
                  onChange={handleFiltroChange}
                  placeholder="Filtrar por CPF/CNPJ"
                  sx={{ mt: 1 }}
                />
              </TableCell>
              <TableCell 
                width="8%"
                sx={{ 
                  height: '40px',
                  padding: '8px 16px',
                  verticalAlign: 'middle',
                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                  textAlign: 'center'
                }}
              >
                <TableSortLabel
                  active={orderBy === "status"}
                  direction={orderBy === "status" ? order : "asc"}
                  onClick={() => handleRequestSort("status")}
                >
                  Status
                </TableSortLabel>
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                  <Select
                    name="status"
                    value={filtros.status || ""}
                    onChange={handleFiltroChange}
                    displayEmpty
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="true">Ativo</MenuItem>
                    <MenuItem value="false">Inativo</MenuItem>
                    <MenuItem value="Bloqueado">Bloqueado</MenuItem>
                    <MenuItem value="Potencial">Potencial</MenuItem>
                  </Select>
                </FormControl>
              </TableCell>
              <TableCell 
                width="8%"
                sx={{ 
                  height: '40px',
                  padding: '8px 16px',
                  verticalAlign: 'middle',
                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                  textAlign: 'center'
                }}
              >
                <TableSortLabel
                  active={orderBy === "lgpd"}
                  direction={orderBy === "lgpd" ? order : "asc"}
                  onClick={() => handleRequestSort("lgpd")}
                >
                  LGPD
                </TableSortLabel>
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                  <Select
                    name="lgpd"
                    value={filtros.lgpd || ""}
                    onChange={handleFiltroChange}
                    displayEmpty
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="true">Consentiu</MenuItem>
                    <MenuItem value="false">Não Consentiu</MenuItem>
                  </Select>
                </FormControl>
              </TableCell>
              <TableCell 
                width="8%"
                sx={{ 
                  height: '40px',
                  padding: '8px 16px',
                  verticalAlign: 'middle',
                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                  textAlign: 'center'
                }}
              >
                Ações
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
                              <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
            ) : clientesPaginados.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Nenhum cliente encontrado.
                </TableCell>
              </TableRow>
            ) : (
              clientesPaginados.map((cliente, index) => (
                <TableRow 
                  key={cliente.id}
                  sx={{
                    backgroundColor: (theme) => 
                      index % 2 === 0 
                        ? theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.02)' 
                          : 'rgba(0, 0, 0, 0.02)'
                        : theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.04)' 
                          : 'rgba(0, 0, 0, 0.04)',
                    '&:hover': {
                      backgroundColor: (theme) => 
                        theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.08)' 
                          : 'rgba(0, 0, 0, 0.08)',
                    }
                  }}
                >
                  <TableCell 
                    sx={{ 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'nowrap',
                      height: '40px', // ✅ Altura fixa para células
                      padding: '8px 16px', // ✅ Padding otimizado para 40px
                      verticalAlign: 'middle',
                      lineHeight: 1.2 // ✅ Alinhamento vertical centralizado
                    }}
                  >
                    {cliente.nome_completo}
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'nowrap',
                      height: '40px',
                      padding: '8px 16px',
                      verticalAlign: 'middle',
                      lineHeight: 1.2
                    }}
                  >
                    {formatarEmailLGPD(cliente.email, true) || '-'}
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'nowrap',
                      height: '40px',
                      padding: '8px 16px',
                      verticalAlign: 'middle',
                      lineHeight: 1.2
                    }}
                  >
                    {formatarTelefoneLGPD(cliente.celular, true) || '-'}
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'nowrap',
                      height: '40px',
                      padding: '8px 16px',
                      verticalAlign: 'middle',
                      lineHeight: 1.2
                    }}
                  >
                    {formatarCPFCNPJLGPD(cliente.cpf_cnpj, cliente.tipo_pessoa, true)}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={cliente.status}>
                      {cliente.status === STATUS_OPTIONS[0].value ? (
                        <ThumbUp className="material-icon-status-active" />
                      ) : cliente.status === STATUS_OPTIONS[1].value ? (
                        <ThumbDown className="material-icon-status-inactive" />
                      ) : (
                        <ThumbDown className="material-icon-status-inactive" />
                      )}
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={cliente.consentimento_lgpd ? "Consentimento LGPD" : "Sem Consentimento LGPD"}>
                      {cliente.consentimento_lgpd ? (
                        <ThumbUp className="material-icon-status-active" />
                      ) : (
                        <ThumbDown className="material-icon-status-inactive" />
                      )}
                    </Tooltip>
                  </TableCell>
                    <TableCell sx={{ 
                      textAlign: 'right', // ✅ Alinha o conteúdo à direita
                      paddingRight: 3 // ✅ Adiciona padding à direita
                    }}>
                      <Box sx={{ 
                        display: 'flex', 
                        gap: 1,
                        justifyContent: 'flex-end' // ✅ Alinha os botões à direita
                      }}>
                      <Tooltip title="Visualizar">
                        <IconButton 
                          size="small" 
                          onClick={() => visualizarCliente(cliente)}
                          sx={{ padding: '4px' }} // ✅ Botão otimizado para linha de 40px
                        >
                          <VisibilityIcon fontSize="small" className="material-icon-visibility" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton 
                          size="small" 
                          onClick={() => abrirFormulario(cliente)}
                          sx={{ padding: '4px' }}
                        >
                          <EditIcon fontSize="small" className="material-icon-edit" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton 
                          size="small" 
                          onClick={() => confirmarExclusao(cliente)}
                          sx={{ padding: '4px' }}
                        >
                          <DeleteIcon fontSize="small" className="material-icon-delete" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginação fora do TableContainer */}
      <TablePagination
        component="div"
        count={clientesFiltrados.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25]}
        labelRowsPerPage="Itens por página"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        sx={{
          backgroundColor: (theme) => theme.palette.background.default,
          borderTop: 'none',
          width: '100%', // ✅ 100% da largura da página
          marginTop: 0
        }}
      />

      {/* Dialog de Cadastro/Edição */}
      <Dialog
        open={openForm}
        onClose={fecharFormulario}
        maxWidth={false}
        fullWidth={false}
        disableEscapeKeyDown
        disableBackdropClick
        disablePortal
        hideBackdrop={false}
        sx={{
          '& .MuiDialog-backdrop': {
            backgroundColor: 'rgba(0, 0, 0, 0.8)'
          }
        }}
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '90vw', md: '900px' },
            height: { xs: '95vh', sm: '90vh', md: '550px' },
            maxWidth: { xs: '95vw', sm: '90vw', md: '900px' },
            maxHeight: { xs: '95vh', sm: '90vh', md: '550px' },
            position: 'relative'
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
          borderBottom: 1,
          borderColor: 'divider',
          padding: 2,
          position: 'sticky',
          top: 0,
          zIndex: 1,
          fontSize: '1.25rem',
          fontWeight: 500
        }}>
          {clienteSelecionado ? "Editar Cliente" : "Novo Cliente"}
        </DialogTitle>
        <DialogContent sx={{ 
          height: 'calc(100% - 140px)',
          overflow: 'auto',
          padding: 2,
          '&.MuiDialogContent-root': {
            paddingTop: 2
          },
          position: 'relative',
          top: 0,
          marginBottom: '60px',
          '& .MuiInputLabel-root': {
            fontSize: '0.875rem'
          },
          '& .MuiInputBase-input': {
            fontSize: '0.875rem'
          },
          '& .MuiFormControlLabel-label': {
            fontSize: '0.875rem'
          },
          '& .MuiTabs-root': {
            borderBottom: 1,
            borderColor: 'divider'
          },
          '& .MuiTab-root': {
            fontSize: '0.875rem',
            textTransform: 'none',
            minWidth: 120
          }
        }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              mb: 2
            }}
          >
            <Tab label="Dados Pessoais" />
            <Tab label="Endereço" />
            <Tab label="Contato" />
            <Tab label="Financeiro" />
            <Tab label="Dados Bancários" />
            <Tab label="Outros" />
          </Tabs>

          {/* Campos do formulário */}
          {activeTab === 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField 
                fullWidth 
                label={
                  <Typography component="span" sx={{ color: 'error.main' }}>
                    Nome Completo
                  </Typography>
                }
                name="nome_completo" 
                value={formData.nome_completo} 
                onChange={handleInputChange}
                onBlur={handleBlur}
                required
                error={formErrors.nome_completo}
                helperText={formErrors.nome_completo ? "Nome é obrigatório" : ""}
                inputRef={nomeCompletoRef}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Tipo de Pessoa</InputLabel>
                  <Select 
                    name="tipo_pessoa" 
                    value={formData.tipo_pessoa} 
                    onChange={handleInputChange} 
                    onBlur={handleBlur}
                    label="Tipo de Pessoa"
                  >
                    <MenuItem value="Física">Física</MenuItem>
                    <MenuItem value="Jurídica">Jurídica</MenuItem>
                  </Select>
                </FormControl>
                <TextField 
                  fullWidth 
                  label={
                    <Typography component="span" sx={{ color: 'error.main' }}>
                      CPF/CNPJ
                    </Typography>
                  }
                  name="cpf_cnpj" 
                  value={formData.cpf_cnpj} 
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  required
                  error={formErrors.cpf_cnpj}
                  helperText={formErrors.cpf_cnpj ? (cpfCnpjErrorMessage || "CPF/CNPJ é obrigatório") : ""}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField 
                  fullWidth 
                  label="Data de Nascimento" 
                  name="data_nascimento" 
                  value={formData.data_nascimento} 
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  placeholder="DD/MM/AAAA"
                />
                <FormControl fullWidth>
                  <InputLabel>Gênero</InputLabel>
                  <Select 
                    name="genero" 
                    value={formData.genero} 
                    onChange={handleInputChange} 
                    onBlur={handleBlur}
                    label="Gênero"
                  >
                    <MenuItem value="Masculino">Masculino</MenuItem>
                    <MenuItem value="Feminino">Feminino</MenuItem>
                    <MenuItem value="Outro">Outro</MenuItem>
                    <MenuItem value="Prefere não informar">Prefere não informar</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Estado Civil</InputLabel>
                  <Select 
                    name="estado_civil" 
                    value={formData.estado_civil} 
                    onChange={handleInputChange} 
                    onBlur={handleBlur}
                    label="Estado Civil"
                  >
                    <MenuItem value="Solteiro(a)">Solteiro(a)</MenuItem>
                    <MenuItem value="Casado(a)">Casado(a)</MenuItem>
                    <MenuItem value="Divorciado(a)">Divorciado(a)</MenuItem>
                    <MenuItem value="Viúvo(a)">Viúvo(a)</MenuItem>
                    <MenuItem value="Separado(a)">Separado(a)</MenuItem>
                    <MenuItem value="União Estável">União Estável</MenuItem>
                  </Select>
                </FormControl>
                <TextField 
                  fullWidth 
                  label="RG" 
                  name="rg" 
                  value={formData.rg} 
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField 
                  fullWidth 
                  label="Profissão" 
                  name="profissao" 
                  value={formData.profissao} 
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                />
                <TextField 
                  fullWidth 
                  label="Empresa" 
                  name="empresa" 
                  value={formData.empresa} 
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField 
                  fullWidth 
                  label="Cargo" 
                  name="cargo" 
                  value={formData.cargo} 
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                />
                <TextField 
                  fullWidth 
                  label="Salário" 
                  name="salario" 
                  value={formData.salario} 
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  placeholder="0,00"
                  helperText="Digite o valor (ex: 2500,50)"
                />
              </Box>
            </Box>
          )}

          {activeTab === 1 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField 
                fullWidth 
                label="CEP" 
                name="cep" 
                value={formData.cep} 
                onChange={handleInputChange}
                onBlur={handleBlur}
                placeholder="00000-000"
              />
              <TextField 
                fullWidth 
                label="Endereço" 
                name="logradouro" 
                value={formData.logradouro} 
                onChange={handleInputChange}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField 
                  fullWidth 
                  label="Número" 
                  name="numero" 
                  value={formData.numero} 
                  onChange={handleInputChange}
                />
                <TextField 
                  fullWidth 
                  label="Complemento" 
                  name="complemento" 
                  value={formData.complemento} 
                  onChange={handleInputChange}
                />
              </Box>
              <TextField 
                fullWidth 
                label="Bairro" 
                name="bairro" 
                value={formData.bairro} 
                onChange={handleInputChange}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField 
                  fullWidth 
                  label="Cidade" 
                  name="cidade" 
                  value={formData.cidade} 
                  onChange={handleInputChange}
                />
                <TextField 
                  fullWidth 
                  label="Estado" 
                  name="estado" 
                  value={formData.estado} 
                  onChange={handleInputChange}
                />
              </Box>
              <TextField 
                fullWidth 
                label="País" 
                name="pais" 
                value={formData.pais} 
                onChange={handleInputChange}
              />
            </Box>
          )}

          {activeTab === 2 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField 
                fullWidth 
                label={pessoaContatoLabel}
                name="pessoa_contato" 
                value={formData.pessoa_contato} 
                onChange={handleInputChange}
                required={calcularIdade(formData.data_nascimento) < 18}
                error={formErrors.pessoa_contato}
                helperText={formErrors.pessoa_contato ? "Pessoa para contato é obrigatória para menores de 18 anos" : ""}
                onBlur={(e) => {
                  if (calcularIdade(formData.data_nascimento) >= 18 && !e.target.value) {
                    setFormData(prev => ({
                      ...prev,
                      pessoa_contato: "o mesmo(a)"
                    }));
                  }
                }}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField 
                  fullWidth 
                  label="Telefone Fixo" 
                  name="telefone_fixo" 
                  value={formData.telefone_fixo} 
                  onChange={handleInputChange}
                />
                <TextField 
                  fullWidth 
                  label={
                    <Typography component="span" sx={{ color: 'error.main' }}>
                      Celular
                    </Typography>
                  }
                  name="celular" 
                  value={formData.celular} 
                  onChange={handleInputChange}
                  required
                  error={formErrors.celular}
                  helperText={formErrors.celular ? "Celular é obrigatório" : ""}
                />
              </Box>
              <TextField 
                fullWidth 
                label={
                  <Typography component="span" sx={{ color: 'error.main' }}>
                    E-mail
                  </Typography>
                }
                name="email" 
                value={formData.email} 
                onChange={handleInputChange}
                onBlur={handleBlur}
                required
                error={formErrors.email}
                helperText={
                  formErrors.email 
                    ? (formData.email && formData.email.trim() ? "E-mail inválido" : "E-mail é obrigatório")
                    : ""
                }
              />
            </Box>
          )}

          {activeTab === 3 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField 
                fullWidth 
                label="Renda Mensal" 
                name="renda_mensal" 
                value={formData.renda_mensal} 
                onChange={handleInputChange}
                onBlur={handleBlur}
                placeholder="0,00"
                helperText="Digite o valor (ex: 5000,00)"
              />
              <TextField 
                fullWidth 
                label="Patrimônio" 
                name="patrimonio" 
                value={formData.patrimonio} 
                onChange={handleInputChange}
                onBlur={handleBlur}
                placeholder="0,00"
                helperText="Digite o valor (ex: 100000,00)"
              />
              <TextField 
                fullWidth 
                label="Limite de Crédito" 
                name="limite_credito" 
                value={formData.limite_credito} 
                onChange={handleInputChange}
                onBlur={handleBlur}
                placeholder="0,00"
                helperText="Digite o valor (ex: 10000,00)"
              />
            </Box>
          )}

          {activeTab === 4 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={copiarDados}
                    onChange={handleCopiarDadosChange}
                  />
                }
                label="Copiar dados do titular"
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField 
                  fullWidth 
                  label="Banco"
                  name="banco" 
                  value={formData.banco} 
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                />
                <TextField 
                  fullWidth 
                  label="Agência"
                  name="agencia" 
                  value={formData.agencia} 
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField 
                  fullWidth 
                  label="Conta"
                  name="conta" 
                  value={formData.conta} 
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                />
                <FormControl fullWidth>
                  <InputLabel>Tipo de Conta</InputLabel>
                  <Select 
                    name="tipo_conta" 
                    value={formData.tipo_conta} 
                    onChange={handleInputChange} 
                    onBlur={handleBlur}
                    label="Tipo de Conta"
                  >
                    <MenuItem value="Corrente">Corrente</MenuItem>
                    <MenuItem value="Poupança">Poupança</MenuItem>
                    <MenuItem value="Salário">Salário</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField 
                  fullWidth 
                  label="Titular da Conta"
                  name="titular_conta" 
                  value={formData.titular_conta} 
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                />
                <TextField 
                  fullWidth 
                  label="CPF/CNPJ do Titular"
                  name="cpf_cnpj_titular" 
                  value={formData.cpf_cnpj_titular} 
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                />
              </Box>
            </Box>
          )}

          {activeTab === 5 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select 
                  name="status" 
                  value={formData.status} 
                  onChange={handleInputChange} 
                  onBlur={handleBlur}
                  label="Status"
                >
                  {STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Checkbox
                    name="consentimento_lgpd"
                    checked={formData.consentimento_lgpd}
                    onChange={handleInputChange}
                  />
                }
                label="Consentimento LGPD"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="is_comissionado"
                    checked={formData.is_comissionado}
                    onChange={handleInputChange}
                  />
                }
                label="É Indicação?"
              />
              {formData.is_comissionado && (
                <>
                  <FormControl fullWidth>
                    <InputLabel>Colaborador Comissionado</InputLabel>
                    <Select 
                      name="comissionado_colaborador_id" 
                      value={formData.comissionado_colaborador_id || ""} 
                      onChange={handleInputChange} 
                      onBlur={handleBlur}
                      label="Colaborador Comissionado"
                    >
                      <MenuItem value="">
                        <em>Selecione um colaborador</em>
                      </MenuItem>
                      {colaboradores.map((colaborador) => (
                        <MenuItem key={colaborador.id} value={colaborador.id}>
                          {colaborador.nome_completo}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel>Cliente Comissionado</InputLabel>
                    <Select 
                      name="comissionado_cliente_id" 
                      value={formData.comissionado_cliente_id || ""} 
                      onChange={handleInputChange} 
                      onBlur={handleBlur}
                      label="Cliente Comissionado"
                    >
                      <MenuItem value="">
                        <em>Selecione um cliente</em>
                      </MenuItem>
                      {clientes.filter(c => c.id !== clienteSelecionado?.id).map((cliente) => (
                        <MenuItem key={cliente.id} value={cliente.id}>
                          {cliente.nome_completo}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}
              <TextField 
                fullWidth 
                label="Observações" 
                name="observacoes" 
                value={formData.observacoes} 
                onChange={handleInputChange}
                multiline
                rows={4}
              />
              <TextField 
                fullWidth 
                label="Tags" 
                name="tags" 
                value={formData.tags} 
                onChange={handleInputChange}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: 2,
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
          borderTop: 1,
          borderColor: 'divider',
          zIndex: 1,
          height: '60px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          '& .MuiButton-root': {
            fontSize: '0.875rem',
            textTransform: 'uppercase',
            fontWeight: 500
          },
          '& .MuiTypography-root': {
            fontSize: '0.875rem'
          }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {formatarDataHora(dataHoraAtual)}
            </Typography>
            {formData.data_nascimento && formData.data_nascimento.length === 10 && (
              <Alert severity="info" sx={{ mb: 0 }}>
                {`${idade} anos`}
              </Alert>
            )}

          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              variant="outlined"
              onClick={() => setActiveTab(prev => Math.max(0, prev - 1))}
              disabled={activeTab === 0}
            >
              Anterior
            </Button>
            <Button
              variant="outlined"
              onClick={() => setActiveTab(prev => Math.min(5, prev + 1))}
              disabled={activeTab === 5}
            >
              Próximo
            </Button>

            <Button 
              variant="contained" 
              color="primary"
              onClick={fecharFormulario}
            >
              FECHAR
            </Button>
            <Button 
              variant="contained" 
              color="primary"
              onClick={salvarCliente}
              disabled={!validateForm() || salvandoCliente}
              startIcon={salvandoCliente ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {salvandoCliente ? "SALVANDO..." : "SALVAR"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Diálogo de Visualização */}
      <Dialog
        open={openView}
        onClose={fecharVisualizacao}
        maxWidth={false}
        fullWidth={false}
        disableEscapeKeyDown
        disableBackdropClick
        disablePortal
        hideBackdrop={false}
        sx={{
          '& .MuiDialog-backdrop': {
            backgroundColor: 'rgba(0, 0, 0, 0.8)'
          }
        }}
        PaperProps={{
          sx: {
            width: '900px',
            height: '550px',
            position: 'relative'
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
          borderBottom: 1,
          borderColor: 'divider',
          padding: 2,
          position: 'sticky',
          top: 0,
          zIndex: 1,
          fontSize: '1.25rem',
          fontWeight: 500,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          Visualizar Cliente
          <IconButton
            onClick={fecharVisualizacao}
            sx={{
              color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
              '&:hover': {
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ 
          height: 'calc(100% - 140px)',
          overflow: 'auto',
          padding: 2,
          '&.MuiDialogContent-root': {
            paddingTop: 2
          },
          position: 'relative',
          top: 0,
          marginBottom: '60px',
          '& .MuiInputLabel-root': {
            fontSize: '0.875rem'
          },
          '& .MuiInputBase-input': {
            fontSize: '0.875rem'
          },
          '& .MuiFormControlLabel-label': {
            fontSize: '0.875rem'
          },
          '& .MuiTabs-root': {
            borderBottom: 1,
            borderColor: 'divider'
          },
          '& .MuiTab-root': {
            fontSize: '0.875rem',
            textTransform: 'none',
            minWidth: 120
          }
        }}>
          {clienteVisualizar && (
            <Box>
              <Tabs 
                value={activeTab} 
                onChange={handleTabChange}
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  mb: 2
                }}
              >
                <Tab label="Dados Pessoais" />
                <Tab label="Endereço" />
                <Tab label="Contato" />
                <Tab label="Financeiro" />
                <Tab label="Dados Bancários" />
                <Tab label="Outros" />
              </Tabs>

              {activeTab === 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <TextField 
                    fullWidth 
                    label={
                      <Typography component="span" sx={{ color: 'error.main' }}>
                        Nome Completo
                      </Typography>
                    }
                    name="nome_completo" 
                    value={clienteVisualizar.nome_completo || ''} 
                    InputProps={{ readOnly: true }}
                    sx={sharedStyles.formField}
                  />
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <FormControl fullWidth sx={sharedStyles.formField}>
                      <InputLabel>Tipo de Pessoa</InputLabel>
                      <Select 
                        name="tipo_pessoa" 
                        value={clienteVisualizar.tipo_pessoa || ''} 
                        label="Tipo de Pessoa"
                        disabled
                      >
                        <MenuItem value="Física">Física</MenuItem>
                        <MenuItem value="Jurídica">Jurídica</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField 
                      fullWidth 
                      label={
                        <Typography component="span" sx={{ color: 'error.main' }}>
                          CPF/CNPJ
                        </Typography>
                      }
                      name="cpf_cnpj" 
                      value={formatarCPFCNPJLGPD(clienteVisualizar.cpf_cnpj, clienteVisualizar.tipo_pessoa, true) || ''} 
                      InputProps={{ readOnly: true }}
                      sx={sharedStyles.formField}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField 
                      fullWidth 
                      label="Data de Nascimento" 
                      name="data_nascimento" 
                      value={clienteVisualizar.data_nascimento ? formatarData(clienteVisualizar.data_nascimento) : ''} 
                      InputProps={{ readOnly: true }}
                      sx={sharedStyles.formField}
                    />
                    <FormControl fullWidth sx={sharedStyles.formField}>
                      <InputLabel>Gênero</InputLabel>
                      <Select 
                        name="genero" 
                        value={clienteVisualizar.genero || ''} 
                        label="Gênero"
                        disabled
                      >
                        <MenuItem value="Masculino">Masculino</MenuItem>
                        <MenuItem value="Feminino">Feminino</MenuItem>
                        <MenuItem value="Outro">Outro</MenuItem>
                        <MenuItem value="Prefere não informar">Prefere não informar</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <FormControl fullWidth sx={sharedStyles.formField}>
                      <InputLabel>Estado Civil</InputLabel>
                      <Select 
                        name="estado_civil" 
                        value={clienteVisualizar.estado_civil || ''} 
                        label="Estado Civil"
                        disabled
                      >
                        <MenuItem value="Solteiro(a)">Solteiro(a)</MenuItem>
                        <MenuItem value="Casado(a)">Casado(a)</MenuItem>
                        <MenuItem value="Divorciado(a)">Divorciado(a)</MenuItem>
                        <MenuItem value="Viúvo(a)">Viúvo(a)</MenuItem>
                        <MenuItem value="Separado(a)">Separado(a)</MenuItem>
                        <MenuItem value="União Estável">União Estável</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField 
                      fullWidth 
                      label="RG" 
                      name="rg" 
                      value={clienteVisualizar.rg ? `***${clienteVisualizar.rg.slice(-3)}` : ''} 
                      InputProps={{ readOnly: true }}
                      sx={sharedStyles.formField}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField 
                      fullWidth 
                      label="Profissão" 
                      name="profissao" 
                      value={clienteVisualizar.profissao || ''} 
                      InputProps={{ readOnly: true }}
                      sx={sharedStyles.formField}
                    />
                    <TextField 
                      fullWidth 
                      label="Empresa" 
                      name="empresa" 
                      value={clienteVisualizar.empresa || ''} 
                      InputProps={{ readOnly: true }}
                      sx={sharedStyles.formField}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField 
                      fullWidth 
                      label="Cargo" 
                      name="cargo" 
                      value={clienteVisualizar.cargo || ''} 
                      InputProps={{ readOnly: true }}
                      sx={sharedStyles.formField}
                    />
                    <TextField 
                      fullWidth 
                      label="Salário" 
                      name="salario" 
                      value={clienteVisualizar.salario ? formatarMoeda(clienteVisualizar.salario) : ''} 
                      InputProps={{ readOnly: true }}
                      sx={sharedStyles.formField}
                    />
                  </Box>
                </Box>
              )}

              {activeTab === 1 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <TextField 
                    fullWidth 
                    label="CEP" 
                    name="cep" 
                    value={clienteVisualizar.cep ? formatarCEP(clienteVisualizar.cep) : ''} 
                    InputProps={{ readOnly: true }}
                    sx={sharedStyles.formField}
                  />
                  <TextField 
                    fullWidth 
                    label="Endereço" 
                    name="logradouro" 
                    value={clienteVisualizar.logradouro || ''} 
                    InputProps={{ readOnly: true }}
                    sx={sharedStyles.formField}
                  />
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField 
                      fullWidth 
                      label="Número" 
                      name="numero" 
                      value={clienteVisualizar.numero || ''} 
                      InputProps={{ readOnly: true }}
                      sx={sharedStyles.formField}
                    />
                    <TextField 
                      fullWidth 
                      label="Complemento" 
                      name="complemento" 
                      value={clienteVisualizar.complemento || ''} 
                      InputProps={{ readOnly: true }}
                      sx={sharedStyles.formField}
                    />
                  </Box>
                  <TextField 
                    fullWidth 
                    label="Bairro" 
                    name="bairro" 
                    value={clienteVisualizar.bairro || ''} 
                    InputProps={{ readOnly: true }}
                    sx={sharedStyles.formField}
                  />
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField 
                      fullWidth 
                      label="Cidade" 
                      name="cidade" 
                      value={clienteVisualizar.cidade || ''} 
                      InputProps={{ readOnly: true }}
                      sx={sharedStyles.formField}
                    />
                    <TextField 
                      fullWidth 
                      label="Estado" 
                      name="estado" 
                      value={clienteVisualizar.estado || ''} 
                      InputProps={{ readOnly: true }}
                      sx={sharedStyles.formField}
                    />
                  </Box>
                  <TextField 
                    fullWidth 
                    label="País" 
                    name="pais" 
                    value={clienteVisualizar.pais || 'Brasil'} 
                    InputProps={{ readOnly: true }}
                    sx={sharedStyles.formField}
                  />
                </Box>
              )}

              {activeTab === 2 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
                  <TextField 
                    fullWidth 
                    label="Pessoa para Contato" 
                    name="pessoa_contato" 
                    value={clienteVisualizar.pessoa_contato || ''} 
                    InputProps={{ readOnly: true }}
                    sx={sharedStyles.formField}
                  />
                  <TextField 
                    fullWidth 
                    label="Telefone Fixo" 
                    name="telefone_fixo" 
                    value={formatarTelefoneLGPD(clienteVisualizar.telefone_fixo, true) || ''} 
                    InputProps={{ readOnly: true }}
                    sx={sharedStyles.formField}
                  />
                  <TextField 
                    fullWidth 
                    label="Celular" 
                    name="celular" 
                    value={formatarTelefoneLGPD(clienteVisualizar.celular, true) || ''} 
                    InputProps={{ readOnly: true }}
                    sx={sharedStyles.formField}
                  />
                  <TextField 
                    fullWidth 
                    label="E-mail" 
                    name="email" 
                    value={formatarEmailLGPD(clienteVisualizar.email, true) || ''} 
                    InputProps={{ readOnly: true }}
                    sx={sharedStyles.formField}
                  />
                </Box>
              )}

              {activeTab === 3 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
                  <TextField 
                    fullWidth 
                    label="Renda Mensal" 
                    name="renda_mensal" 
                    value={clienteVisualizar.renda_mensal ? formatarMoeda(clienteVisualizar.renda_mensal) : ''} 
                    InputProps={{ readOnly: true }}
                    sx={sharedStyles.formField}
                  />
                  <TextField 
                    fullWidth 
                    label="Patrimônio" 
                    name="patrimonio" 
                    value={clienteVisualizar.patrimonio ? formatarMoeda(clienteVisualizar.patrimonio) : ''} 
                    InputProps={{ readOnly: true }}
                    sx={sharedStyles.formField}
                  />
                  <TextField 
                    fullWidth 
                    label="Limite de Crédito" 
                    name="limite_credito" 
                    value={clienteVisualizar.limite_credito ? formatarMoeda(clienteVisualizar.limite_credito) : ''} 
                    InputProps={{ readOnly: true }}
                    sx={sharedStyles.formField}
                  />
                </Box>
              )}

              {activeTab === 4 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
                  <TextField 
                    fullWidth 
                    label="Banco" 
                    name="banco" 
                    value={clienteVisualizar.banco || ''} 
                    InputProps={{ readOnly: true }}
                    sx={sharedStyles.formField}
                  />
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField 
                      fullWidth 
                      label="Agência" 
                      name="agencia" 
                      value={formatarDadosBancariosLGPD(clienteVisualizar.agencia, true) || ''} 
                      InputProps={{ readOnly: true }}
                      sx={sharedStyles.formField}
                    />
                    <TextField 
                      fullWidth 
                      label="Conta" 
                      name="conta" 
                      value={formatarDadosBancariosLGPD(clienteVisualizar.conta, true) || ''} 
                      InputProps={{ readOnly: true }}
                      sx={sharedStyles.formField}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField 
                      fullWidth 
                      label="Tipo de Conta" 
                      name="tipo_conta" 
                      value={clienteVisualizar.tipo_conta || ''} 
                      InputProps={{ readOnly: true }}
                      sx={sharedStyles.formField}
                    />
                    <TextField 
                      fullWidth 
                      label="Titular da Conta" 
                      name="titular_conta" 
                                          value={mascararNome(clienteVisualizar.titular_conta)}  
                      InputProps={{ readOnly: true }}
                      sx={sharedStyles.formField}
                    />
                  </Box>
                  <TextField 
                    fullWidth 
                    label="CPF/CNPJ do Titular" 
                    name="cpf_cnpj_titular" 
                                          value={formatarCPFCNPJLGPD(clienteVisualizar.cpf_cnpj_titular, clienteVisualizar.tipo_pessoa, true) || ''}  
                    InputProps={{ readOnly: true }}
                    sx={sharedStyles.formField}
                  />
                </Box>
              )}

              {activeTab === 5 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField 
                      fullWidth 
                      label="Status" 
                      name="status" 
                      value={clienteVisualizar.status || ''} 
                      InputProps={{ readOnly: true }}
                      sx={sharedStyles.formField}
                    />
                    <FormControl fullWidth sx={sharedStyles.formField}>
                      <InputLabel>Consentimento LGPD</InputLabel>
                      <Select 
                        name="consentimento_lgpd" 
                        value={clienteVisualizar.consentimento_lgpd ? 'Sim' : 'Não'} 
                        label="Consentimento LGPD"
                        disabled
                      >
                        <MenuItem value="Sim">Sim</MenuItem>
                        <MenuItem value="Não">Não</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <FormControl fullWidth sx={sharedStyles.formField}>
                    <InputLabel>É Indicação</InputLabel>
                    <Select 
                      name="is_comissionado" 
                      value={clienteVisualizar.is_comissionado ? 'Sim' : 'Não'} 
                      label="É Indicação"
                      disabled
                    >
                      <MenuItem value="Sim">Sim</MenuItem>
                      <MenuItem value="Não">Não</MenuItem>
                    </Select>
                  </FormControl>
                  {clienteVisualizar.is_comissionado && (
                    <>
                      <TextField 
                        fullWidth 
                        label="Colaborador Comissionado" 
                        name="comissionado_colaborador" 
                        value={colaboradores.find(c => c.id === clienteVisualizar.comissionado_colaborador_id)?.nome_completo || ''} 
                        InputProps={{ readOnly: true }}
                        sx={sharedStyles.formField}
                      />
                      <TextField 
                        fullWidth 
                        label="Cliente Comissionado" 
                        name="comissionado_cliente" 
                        value={clientes.find(c => c.id === clienteVisualizar.comissionado_cliente_id)?.nome_completo || ''} 
                        InputProps={{ readOnly: true }}
                        sx={sharedStyles.formField}
                      />
                    </>
                  )}
                  <TextField 
                    fullWidth 
                    label="Observações" 
                    name="observacoes" 
                    value={clienteVisualizar.observacoes || ''} 
                    InputProps={{ readOnly: true }}
                    multiline
                    rows={4}
                    sx={sharedStyles.formField}
                  />
                  <TextField 
                    fullWidth 
                    label="Tags" 
                    name="tags" 
                    value={clienteVisualizar.tags || ''} 
                    InputProps={{ readOnly: true }}
                    sx={sharedStyles.formField}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: 2,
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
          borderTop: 1,
          borderColor: 'divider',
          zIndex: 1,
          height: '60px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          '& .MuiButton-root': {
            fontSize: '0.875rem',
            textTransform: 'uppercase',
            fontWeight: 500
          },
          '& .MuiTypography-root': {
            fontSize: '0.875rem'
          }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {formatarDataHora(dataHoraAtual)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              variant="contained" 
              color="primary"
              onClick={fecharVisualizacao}
            >
              FECHAR
            </Button>
            {clienteVisualizar && (
              <Button 
                variant="contained" 
                color="primary"
                onClick={() => imprimirCliente(clienteVisualizar)}
                startIcon={<PrintIcon className="material-icon-print" />}
              >
                IMPRIMIR
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* Dialog de Diagnóstico */}
      <DiagnosticoCliente
        open={openDiagnostico}
        onClose={() => setOpenDiagnostico(false)}
        formData={formData}
        clientes={clientes}
        colaboradores={colaboradores}
      />

      <Dialog open={openDeleteConfirm} onClose={fecharConfirmacaoExclusao} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ 
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
          borderBottom: 1,
          borderColor: 'divider',
          padding: 2,
          fontSize: '1.25rem',
          fontWeight: 500
        }}>
          Confirmar Exclusão
        </DialogTitle>
        <DialogContent sx={{ padding: 2 }}>
          <Typography>
            Tem certeza que deseja excluir este cliente?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ 
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
          borderTop: 1,
          borderColor: 'divider',
          padding: 2,
          '& .MuiButton-root': {
            fontSize: '0.875rem',
            textTransform: 'uppercase',
            fontWeight: 500
          }
        }}>
          <Button onClick={fecharConfirmacaoExclusao}>CANCELAR</Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={deletarCliente} 
            disabled={loading}
          >
            EXCLUIR
          </Button>
        </DialogActions>
      </Dialog>
      </Box> {/* Fechamento do Box interno com margens de 1,5% */}
    </Box>
    </AcessoRestrito>
  );
}